import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  BriefcaseMedical,
  Check,
  ChevronRight,
  ClipboardList,
  Globe2,
  MapPin,
  MessageCircle,
  Navigation,
  Pencil,
  Send,
  Sparkles,
  Stethoscope,
  Wallet,
} from "lucide-react";
import "./CarePriceUI.scss";
import PatientJourneyStrip from "../../../../components/PatientJourneyStrip/PatientJourneyStrip";

import { getSocket } from "../../../../Api/socket";

import {
  clearMatchResults,
  clearPatientFlowError,
  clearPatientFlowSuccess,
  clearPreviewProviders,
} from "../../../../Features/Postes/Slice/patientFlowSlice";

import {
  getSelections,
  getLowestPrice,
  getPreviewProviders,
  savePatientLocation,
  createMatchRequest,
  getProviderCount,
  getMatchResults,
  respondToBid,
  bookAppointment,
  patientCounterBid,
} from "../../../../Features/Postes/Thunk/patientFlowThunk";
import BidHistoryChat from "../../../../components/BidHistoryChat/BidHistoryChat";
import {
  bidAmountDigitsOnly,
  bidAmountInputToNumber,
  bidAmountRawHasDisallowedSymbols,
  formatIndianBidAmountInput,
  validateBidAmountDigitsNotPhoneLike,
} from "../../../../utils/bidAmountInput";
import { matchBidPatientOfferAmount, matchBidTableAmount } from "../../../../utils/matchBidDisplayAmount";
import API from "../../../../Api/Api";
import { ENDPOINTS } from "../../../../Api/Endpoints";
import { SkeletonBlock } from "../../../../components/Skeleton/Skeleton";
/** UI matches goal mocks (5–50 km). Backend still accepts up to 100 km if sent from elsewhere. */
const WITHIN_ZONE_SLIDER_MAX_KM = 50;
/** Preview POST should not fire on every range / pincode input tick. */
const PREVIEW_RADIUS_DEBOUNCE_MS = 400;
const PREVIEW_PINCODE_DEBOUNCE_MS = 400;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const fmtINR = (n) => `Rs.${Number(n || 0).toLocaleString("en-IN")}`;
/** Design-mock INR with ₹ symbol */
const fmtINRGoal = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function formatCarePriceLocationArea(locationScope, searchPincode, withinZoneRadiusKm) {
  if (locationScope === "withinZone") return `${withinZoneRadiusKm} km around your location`;
  if (locationScope === "locationWise") {
    const p = String(searchPincode || "").trim();
    return p ? `Pincode ${p}` : "Pincode area";
  }
  if (locationScope === "allOverWestBengal") return "West Bengal (statewide)";
  return String(locationScope || "—").replace(/_/g, " ") || "—";
}

/** Patient-facing line for `providerAction` (BidLog: pending | countered | accepted_patient_price | rejected). */
function patientFacingProviderActionLabel(providerAction) {
  const a = String(providerAction || "pending").toLowerCase();
  if (a === "pending") return "Awaiting provider response";
  if (a === "accepted_patient_price") return "Accepted your offer";
  if (a === "countered") return "Offered a different price";
  if (a === "rejected") return "Unavailable for this request";
  const fallback = String(providerAction || "").replace(/_/g, " ").trim();
  return fallback || "Awaiting provider response";
}

/** When the provider never replied within the response window, show clear “ended” copy instead of “awaiting”. */
function patientFacingProviderRowStatusLabel(providerAction, openingBidExpired) {
  const a = String(providerAction || "pending").toLowerCase();
  if (openingBidExpired && a === "pending") {
    return "Response window ended — no reply yet";
  }
  return patientFacingProviderActionLabel(providerAction);
}

/** “Nice” step for slider / nudges so small ranges use ₹50-style steps and lakh-scale ranges use meaningful jumps. */
const getNiceMoneyStep = (span, targetPieces) => {
  const spanN = Math.max(Number(span) || 0, 1);
  const raw = Math.max(spanN / Math.max(Number(targetPieces) || 1, 1), 1);
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const mult = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return Math.max(1, Math.round(mult * pow));
};

const snapBidToSliderStep = (value, min, max) => {
  const span = Math.max(max - min, 1);
  const step = getNiceMoneyStep(span, 100);
  const c = clamp(value, min, max);
  const snapped = min + Math.round((c - min) / step) * step;
  return clamp(snapped, min, max);
};

/** Match request `categoryLabel` is often "Category · Sub A, Sub B" — split for Step 1 display. */
const splitMatchRequestCategoryLabel = (label) => {
  const s = typeof label === "string" ? label.trim() : "";
  if (!s) return { category: "", sub: "" };
  const idx = s.indexOf(" · ");
  if (idx === -1) return { category: s, sub: "" };
  return { category: s.slice(0, idx).trim(), sub: s.slice(idx + 3).trim() };
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const pushProcedurePathPart = (list, value) => {
  const text = String(value || "").trim();
  if (!text) return;
  if (list.length && list[list.length - 1].toLowerCase() === text.toLowerCase()) return;
  list.push(text);
};

/** Split `categoryLabel` / `categoryBreadcrumb` on middle dot or " > " (same as patient dashboard). */
const parseProcedurePathSegments = (value) => {
  const s = String(value || "").trim();
  if (!s) return [];
  const dotParts = s.split(/\s*·\s*/).map((p) => p.trim()).filter(Boolean);
  if (dotParts.length > 1) return dotParts;
  return s.split(/\s*>\s*/).map((p) => p.trim()).filter(Boolean);
};

/**
 * Best L1–L5-style path: GET /selections is often shallow (category + leaf only);
 * match-request `categoryBreadcrumb` has the full chain from the DB.
 */
const procedurePathPartsFromRequestLike = (reqLike) => {
  if (!reqLike) return [];
  const candidates = [];
  const expandedParts = [];
  for (const block of safeArray(reqLike.selectionsExpanded)) {
    pushProcedurePathPart(expandedParts, block?.categoryName);
    for (const sub of safeArray(block?.subCategories)) {
      pushProcedurePathPart(expandedParts, sub?.subCategoryName || sub?.name);
    }
  }
  if (expandedParts.length) candidates.push(expandedParts);
  const labelParts = parseProcedurePathSegments(reqLike.categoryLabel);
  if (labelParts.length) candidates.push(labelParts);
  const crumbParts = parseProcedurePathSegments(reqLike.categoryBreadcrumb);
  if (crumbParts.length) candidates.push(crumbParts);
  if (!candidates.length) return [];
  return candidates.reduce((best, cur) => (cur.length > best.length ? cur : best), []);
};
const toFiniteNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pickId = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const pickName = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
};

/** Axios GET detail response → match request row (handles `{ request }` and `{ data: { request } }`). */
const pickMatchRequestRowFromDetailResponse = (res) => {
  const data = res?.data ?? {};
  return (
    data.request ??
    data.data?.request ??
    res?.request ??
    data.matchRequest ??
    data.data?.matchRequest ??
    null
  );
};

/** Subcategory ids on a match-request detail row (for comparing to route “new procedure” intent). */
const collectDetailRowSubCategoryIds = (row) => {
  if (!row) return [];
  const out = [];
  for (const id of safeArray(row.selectedSubCategoryIds)) {
    const s = String(id || "").trim();
    if (s) out.push(s);
  }
  for (const block of safeArray(row.selections)) {
    for (const sid of safeArray(block?.subCategoryIds)) {
      const p =
        typeof sid === "object" && sid
          ? pickId(sid._id, sid.id, sid.subCategoryId)
          : String(sid || "");
      const s = String(p || "").trim();
      if (s) out.push(s);
    }
  }
  return Array.from(new Set(out));
};

const getSelectionList = (selections) => {
  if (Array.isArray(selections)) return selections;
  if (Array.isArray(selections?.items)) return selections.items;
  if (Array.isArray(selections?.data?.selections)) return selections.data.selections;
  if (Array.isArray(selections?.data)) return selections.data;
  if (Array.isArray(selections?.selections)) return selections.selections;
  return [];
};

const getSelectionSubCategoryIds = (selectionItem) => {
  const candidates = [];
  const sources = [
    selectionItem?.subCategoryIds,
    selectionItem?.subcategoryIds,
    selectionItem?.subCategories,
  ];
  for (const source of sources) {
    for (const entry of safeArray(source)) {
      const id = pickId(
        entry?.subCategoryId,
        entry?._id,
        entry?.id,
        typeof entry === "string" || typeof entry === "number" ? entry : ""
      );
      if (id !== undefined && id !== null && id !== "") {
        candidates.push(String(id));
      }
    }
  }
  return Array.from(new Set(candidates));
};

const getSelectionCategoryId = (selectionItem) => {
  if (!selectionItem) return "";
  return pickId(
    selectionItem?.category?._id,
    selectionItem?.category?.id,
    selectionItem?.selectedCategory?._id,
    selectionItem?.selectedCategory?.id,
    selectionItem?.categoryId?._id,
    selectionItem?.categoryId?.id,
    selectionItem?.categoryId
  );
};

const getSelectionSubCategoryId = (selectionItem) => {
  if (!selectionItem) return "";
  const fromExpanded = safeArray(selectionItem?.subCategories)?.[0];
  return pickId(
    fromExpanded?.subCategoryId,
    fromExpanded?._id,
    fromExpanded?.id,
    selectionItem?.subCategory?._id,
    selectionItem?.subCategory?.id,
    selectionItem?.selectedSubCategory?._id,
    selectionItem?.selectedSubCategory?.id,
    selectionItem?.subCategoryId,
    safeArray(selectionItem?.subCategoryIds)?.[0]?._id,
    safeArray(selectionItem?.subCategoryIds)?.[0]?.id,
    safeArray(selectionItem?.subCategoryIds)?.[0]
  );
};

const getSelectedCategory = (selectionItem) => {
  if (!selectionItem) return null;

  if (selectionItem.category || selectionItem.selectedCategory) {
    return selectionItem.category || selectionItem.selectedCategory;
  }

  // GET /app/selections expanded shape: { categoryId, categoryName, subCategories: [...] }
  if (selectionItem.categoryName || selectionItem.categoryId) {
    return {
      _id:
        selectionItem.categoryId?._id ||
        selectionItem.categoryId?.id ||
        selectionItem.categoryId,
      id:
        selectionItem.categoryId?._id ||
        selectionItem.categoryId?.id ||
        selectionItem.categoryId,
      name: pickName(selectionItem.categoryName, selectionItem.categoryId?.name),
    };
  }

  return null;
};

const getSelectedSubCategory = (selectionItem) => {
  if (!selectionItem) return null;

  const fromObject =
    selectionItem.subCategory ||
    selectionItem.selectedSubCategory ||
    selectionItem.subcategory ||
    null;

  if (fromObject) return fromObject;

  const expandedFirst = safeArray(selectionItem.subCategories)[0];
  if (expandedFirst && typeof expandedFirst === "object") {
    const sid = pickId(
      expandedFirst.subCategoryId,
      expandedFirst._id,
      expandedFirst.id
    );
    const sname = pickName(expandedFirst.subCategoryName, expandedFirst.name);
    return {
      ...expandedFirst,
      _id: sid || expandedFirst._id,
      id: sid || expandedFirst.id,
      subCategoryId: expandedFirst.subCategoryId || sid,
      name: sname,
      subCategoryName: expandedFirst.subCategoryName || sname,
    };
  }

  const subIds =
    selectionItem.subCategoryIds ||
    selectionItem.subcategoryIds ||
    selectionItem.subCategories ||
    [];

  if (Array.isArray(subIds) && subIds.length > 0) {
    const first = subIds[0];

    if (typeof first === "object" && first !== null) return first;

    return {
      id: first,
      _id: first,
      name: selectionItem.subCategoryName || selectionItem.subcategoryName || "",
    };
  }

  return null;
};

/** When admin/API gives no usable bounds, patient can still bid across this range (₹0 – ₹30,00,000). */
const FALLBACK_BID_MIN = 0;
const FALLBACK_BID_MAX = 3000000;

const normalizePriceInfo = (priceInfo) => {
  const source = priceInfo?.data || priceInfo?.item || priceInfo || {};

  let lowest = toFiniteNumber(
    source?.lowestPrice ?? source?.minPrice ?? source?.minimumPrice
  );
  let highest = toFiniteNumber(
    source?.highestPrice ?? source?.maxPrice ?? source?.maximumPrice
  );
  const averagePrice = toFiniteNumber(
    source?.averagePrice ?? source?.average_price
  );

  const initialBidRaw = toFiniteNumber(
    source?.initial_Bid_amount ??
      source?.initialBidAmount ??
      source?.suggestedInitialBid ??
      source?.averagePrice
  );

  if (!Number.isFinite(lowest) || !Number.isFinite(highest)) {
    if (averagePrice != null && averagePrice > 0) {
      lowest = Math.max(1, Math.floor(averagePrice * 0.4));
      highest = Math.max(lowest + 1, Math.ceil(averagePrice * 1.6));
    } else if (initialBidRaw != null && initialBidRaw > 0) {
      lowest = Math.max(1, Math.floor(initialBidRaw * 0.4));
      highest = Math.max(lowest + 1, Math.ceil(initialBidRaw * 1.6));
    } else {
      lowest = FALLBACK_BID_MIN;
      highest = FALLBACK_BID_MAX;
    }
  }

  if (highest < lowest) {
    const t = lowest;
    lowest = highest;
    highest = t;
  }

  // Admin seed / bad data: 0–0 or equal bounds — slider and bids need a real range.
  if (lowest <= 0 && highest <= 0) {
    if (averagePrice != null && averagePrice > 0) {
      lowest = Math.max(1, Math.floor(averagePrice * 0.4));
      highest = Math.max(lowest + 1, Math.ceil(averagePrice * 1.6));
    } else if (initialBidRaw != null && initialBidRaw > 0) {
      lowest = Math.max(1, Math.floor(initialBidRaw * 0.4));
      highest = Math.max(lowest + 1, Math.ceil(initialBidRaw * 1.6));
    } else {
      lowest = FALLBACK_BID_MIN;
      highest = FALLBACK_BID_MAX;
    }
  } else if (lowest <= 0 && highest > 0) {
    lowest = Math.max(1, Math.floor(highest * 0.05));
  }

  if (highest <= lowest) {
    if (averagePrice != null && averagePrice > 0) {
      lowest = Math.max(1, Math.floor(averagePrice * 0.85));
      highest = Math.max(lowest + 100, Math.ceil(averagePrice * 1.15));
    } else {
      highest = lowest + Math.max(1000, Math.round(lowest * 0.2));
    }
  }

  const initialBid =
    initialBidRaw == null
      ? Math.round((lowest + highest) / 2)
      : clamp(initialBidRaw, lowest, highest);

  return {
    lowestPrice: lowest,
    highestPrice: highest,
    initialBidAmount: clamp(initialBid, lowest, highest),
  };
};

const normalizePreviewList = (previewProviders) => {
  if (!previewProviders) return [];

  const source = previewProviders?.data || previewProviders;
  const doctors = safeArray(source?.doctors);
  const hospitals = safeArray(source?.hospitals);
  const items = safeArray(source?.items);

  const mixed = [...doctors, ...hospitals, ...items];

  return mixed.map((item, index) => {
    const profile = item?.profile || {};
    const role = String(item?.role || (item?.hospitalName ? "hospital" : "doctor") || "doctor").toLowerCase();
    const name = pickName(item?.name, profile?.fullName, item?.doctorName, item?.hospitalName, "Doctor");
    const avatarUrl = pickName(item?.image, item?.avatarUrl, item?.avatar, item?.hospitalImage, item?.hospitalAvatar, "");
    const adminRating = toFiniteNumber(item?.adminRating ?? item?.rating ?? profile?.rating);
    const patientRatingAverage = toFiniteNumber(item?.patientRatingAverage);
    const patientRatingCount =
      Number(item?.patientRatingCount ?? 0) || 0;
    const distanceKm = toFiniteNumber(item?.distanceKm ?? item?.distKm ?? item?.distance);
    const providerId = pickId(item?._id, item?.id);

    return {
      id: pickId(item?._id, item?.id, `preview-${index}`),
      providerId,
      role,
      name,
      avatarUrl,
      spec: pickName(
        item?.specialization,
        item?.specialty,
        profile?.specialization,
        profile?.specialty,
        item?.type,
        "Specialist"
      ),
      exp:
        Number(
          item?.experienceYears ??
            item?.experience ??
            profile?.experienceYears ??
            0
        ) || 0,
      adminRating: adminRating ?? null,
      rating: adminRating ?? 0,
      patientRatingAverage,
      patientRatingCount,
      distKm: distanceKm,
      price: null,
      bidId: null,
      providerAction: "pending",
      patientAction: "pending",
      raw: item,
    };
  });
};

const normalizeMatches = (matches, matchRequestAffordability) => {
  const source = matches?.data || matches || {};

  const list = Array.isArray(matches)
    ? matches
    : Array.isArray(source?.items)
    ? source.items
    : Array.isArray(source?.matches)
    ? source.matches
    : Array.isArray(source?.bids)
    ? source.bids
    : Array.isArray(source?.doctors) || Array.isArray(source?.hospitals)
    ? [...safeArray(source?.doctors), ...safeArray(source?.hospitals)]
    : [];

  return list.map((item, index) => {
    const provider = item?.doctor || item?.provider || item?.hospital || item || {};
    const bid = item?.bid || {};
    const profile = provider?.profile || {};

    const bidId = pickId(
      item?.bidId,
      bid?._id,
      bid?.id,
      item?._id,
      item?.id
    );

    const providerAction = pickName(item?.providerAction, bid?.providerAction, item?.status, "pending");
    const patientAction = pickName(item?.patientAction, bid?.patientAction, item?.patientResponse, "pending");

    const mergedForBidAmount = {
      providerAction,
      patientAction,
      reverseBidAmount: item?.reverseBidPrice ?? item?.reverseBidAmount ?? bid?.reverseBidAmount,
      reverseBidPrice: item?.reverseBidPrice,
      patientCounterAmount: item?.patientCounterAmount ?? bid?.patientCounterAmount,
      affordabilityPrice: bid?.affordabilityPrice ?? item?.affordabilityPrice,
      yourBidPrice: item?.yourBidPrice,
      displayPrice: item?.displayPrice,
      matchRequest: item?.matchRequest,
    };

    const patientOfferAmount = toFiniteNumber(
      matchBidPatientOfferAmount(mergedForBidAmount, matchRequestAffordability)
    );

    const listPrice = matchBidTableAmount(mergedForBidAmount, matchRequestAffordability);
    const amount =
      listPrice != null && Number.isFinite(listPrice) && listPrice > 0 ? listPrice : 0;

    const role = String(item?.role || provider?.role || (item?.hospitalName ? "hospital" : "doctor") || "doctor").toLowerCase();
    const name = pickName(
      item?.name,
      provider?.name,
      profile?.fullName,
      item?.doctorName,
      item?.hospitalName,
      item?.hospitalName || provider?.hospitalName,
      "Provider"
    );
    const avatarUrl = pickName(
      item?.image,
      item?.hospitalImage,
      provider?.image,
      provider?.avatar,
      item?.avatarUrl,
      ""
    );
    const adminRating = toFiniteNumber(item?.adminRating ?? provider?.adminRating ?? item?.rating ?? provider?.rating ?? profile?.rating);
    const patientRatingAverage = toFiniteNumber(
      item?.patientRatingAverage ?? provider?.patientRatingAverage
    );
    const patientRatingCount =
      Number(item?.patientRatingCount ?? provider?.patientRatingCount ?? 0) || 0;
    const distanceKm = toFiniteNumber(item?.distanceKm ?? provider?.distanceKm ?? item?.distKm ?? item?.distance);
    const openingBidExpired = Boolean(item?.openingBidExpired);

    return {
      raw: item,
      id: bidId || `bid-${index}`,
      bidId: bidId || `bid-${index}`,
      role,
      providerId: pickId(provider?._id, provider?.id, item?.doctorId, item?.hospitalId),
      name,
      avatarUrl,
      spec: pickName(
        provider?.specialization,
        provider?.specialty,
        profile?.specialization,
        profile?.specialty,
        item?.specialization,
        "Specialist"
      ),
      exp:
        Number(
          item?.yearsOfExperience ??
            provider?.yearsOfExperience ??
            provider?.experienceYears ??
            provider?.experience ??
            profile?.experienceYears ??
            item?.experienceYears ??
            0
        ) || 0,
      degree: pickName(item?.degree, provider?.degree, profile?.degree, ""),
      hospitalClinicName: pickName(
        item?.hospitalClinicName,
        provider?.hospitalClinicName,
        item?.cityOfPractice,
        item?.city,
        ""
      ),
      subCategoryName: pickName(item?.subCategoryName, ""),
      adminRating: adminRating ?? null,
      rating: adminRating ?? 0,
      patientRatingAverage,
      patientRatingCount,
      distKm: distanceKm,
      price: amount || null,
      patientOfferAmount: patientOfferAmount ?? null,
      providerAction,
      patientAction,
      openingBidExpired,
      conversationId: pickId(item?.conversationId, bid?.conversationId) || "",
    };
  });
};

export default function CarePriceUI({ initialMatchRequestId } = {}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const priceSliderRef = useRef(null);
  const socketRef = useRef(null);
  const geoLocateInFlightRef = useRef(false);

  const LAST_MATCH_REQUEST_ID_KEY = "healbid:careprice:lastMatchRequestId";

  const {
    error,
    successMessage,
    selections,
    priceInfo,
    previewProviders,
    matchRequest,
    matches,
    matchResultsRaw,
    patientLocation,
  } = useSelector((state) => state.patientFlow);

  const [price, setPrice] = useState(0);
  const [isPriceSliding, setIsPriceSliding] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [activeMatchRequestId, setActiveMatchRequestId] = useState("");
  const [restoredSubCategoryId, setRestoredSubCategoryId] = useState(null);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [submitRequestError, setSubmitRequestError] = useState("");
  const [reverseBidModal, setReverseBidModal] = useState(null); // { bidId, currentBid, amount, patientName }
  const [interactModal, setInteractModal] = useState(null); // { bidId, name, ...providerItem } for Interact chat
  const [counterSubmitting, setCounterSubmitting] = useState(false);
  const [reverseBidError, setReverseBidError] = useState("");
  /** After bidding is live, default to summary + doctor list; Edit expands the full form again. */
  const [careGlimpseMode, setCareGlimpseMode] = useState(false);

  const [patientCoords, setPatientCoords] = useState({
    patientLat: "",
    patientLng: "",
  });

  const [locationPermissionError, setLocationPermissionError] = useState("");
  const [locationScope, setLocationScope] = useState("withinZone");
  const [packageProcedureOt, setPackageProcedureOt] = useState(true);
  const [packageImplants, setPackageImplants] = useState(false);
  const [packageHospitalStay, setPackageHospitalStay] = useState(false);
  const [searchPincode, setSearchPincode] = useState("");
  /** Pincode sent to preview API — debounced while typing. */
  const [previewSearchPincode, setPreviewSearchPincode] = useState("");
  const [locationRequesting, setLocationRequesting] = useState(false);
  const [geoPermissionState, setGeoPermissionState] = useState("prompt");

  /** Within-zone search radius (km). Sent to preview + match request. */
  const [withinZoneRadiusKm, setWithinZoneRadiusKm] = useState(20);
  /** Radius used for GET preview API — debounced while dragging the km slider. */
  const [previewWithinZoneRadiusKm, setPreviewWithinZoneRadiusKm] = useState(20);
  const LAST_PATIENT_COORDS_KEY = "healbid:careprice:patientCoords";

  /** Mobile wizard: 1 procedure · 2 location · 3 budget (desktop shows all). */
  const [careWizardStep, setCareWizardStep] = useState(2);
  const [layoutIsWide, setLayoutIsWide] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 960px)").matches : false
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 960px)");
    const onChange = () => setLayoutIsWide(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (withinZoneRadiusKm > WITHIN_ZONE_SLIDER_MAX_KM || withinZoneRadiusKm < 5) {
      setWithinZoneRadiusKm(
        Math.round(
          Math.max(5, Math.min(WITHIN_ZONE_SLIDER_MAX_KM, Number(withinZoneRadiusKm) || 20))
        )
      );
    }
  }, [withinZoneRadiusKm]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setPreviewWithinZoneRadiusKm(withinZoneRadiusKm);
    }, PREVIEW_RADIUS_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [withinZoneRadiusKm]);

  useEffect(() => {
    if (locationScope !== "locationWise") {
      setPreviewSearchPincode("");
      return undefined;
    }
    const trimmed = searchPincode.trim();
    if (!trimmed) {
      setPreviewSearchPincode("");
      return undefined;
    }
    const id = window.setTimeout(() => {
      setPreviewSearchPincode(trimmed);
    }, PREVIEW_PINCODE_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchPincode, locationScope]);

  /**
   * Procedure from GET /app/match-requests/detail — request-flow patients often have this
   * while GET /selections is still empty or out of sync.
   * `undefined` = not fetched yet; `null` = no active id or fetch returned nothing useful.
   */
  const [matchRequestProcedureHydration, setMatchRequestProcedureHydration] = useState();

  const routeMatchRequestIdFromState = String(location?.state?.matchRequestId || "").trim();

  const [priceLimits, setPriceLimits] = useState(() => {
    const prefetched = location?.state?.prefetchedPriceInfo;
    if (!prefetched) return null;

    const normalized = normalizePriceInfo(prefetched);
    return {
      min: normalized.lowestPrice,
      max: normalized.highestPrice,
      initial: normalized.initialBidAmount,
    };
  });

  const priceBoundsReady = !!(priceLimits && Number.isFinite(priceLimits.min) && Number.isFinite(priceLimits.max));

  const { priceSliderStep, priceNudgeStep } = useMemo(() => {
    if (!priceBoundsReady || !priceLimits) {
      return { priceSliderStep: 1, priceNudgeStep: 50 };
    }
    const span = Math.max(priceLimits.max - priceLimits.min, 1);
    const slider = getNiceMoneyStep(span, 100);
    const nudge = Math.max(getNiceMoneyStep(span, 28), slider);
    return { priceSliderStep: slider, priceNudgeStep: nudge };
  }, [priceBoundsReady, priceLimits]);

  // If CarePriceGate provides a match request id, resume immediately.
  useEffect(() => {
    if (!initialMatchRequestId) return;
    const reqId = String(initialMatchRequestId).trim();
    if (!reqId) return;

    try {
      window.localStorage.setItem(LAST_MATCH_REQUEST_ID_KEY, reqId);
    } catch (_) {}

    setActiveMatchRequestId(reqId);
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(reqId));
        const req = pickMatchRequestRowFromDetailResponse(res);
        const w = String(req?.requestWorkflowStatus || "").toUpperCase();
        const published = Boolean(req?.biddingPublished);
        const biddingLive =
          published || ["BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(w);
        if (cancelled) return;
        if (biddingLive) {
          setRequestSubmitted(true);
          dispatch(getProviderCount(reqId));
          dispatch(getMatchResults(reqId));
        } else {
          setRequestSubmitted(false);
          dispatch(clearMatchResults());
        }
      } catch {
        if (!cancelled) {
          setRequestSubmitted(false);
          dispatch(clearMatchResults());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialMatchRequestId, dispatch]);

  useEffect(() => {
    const mrId = String(
      routeMatchRequestIdFromState ||
        activeMatchRequestId ||
        initialMatchRequestId ||
        ""
    ).trim();
    if (!mrId) {
      setMatchRequestProcedureHydration(null);
      return;
    }
    let cancelled = false;
    API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(mrId))
      .then((res) => {
        if (cancelled) return;
        const req = pickMatchRequestRowFromDetailResponse(res);
        if (!req) {
          setMatchRequestProcedureHydration(null);
          return;
        }
        setMatchRequestProcedureHydration({
          selectionsExpanded: Array.isArray(req.selectionsExpanded) ? req.selectionsExpanded : [],
          categoryLabel: String(req.categoryLabel || "").trim(),
          categoryBreadcrumb: String(req.categoryBreadcrumb || "").trim(),
          selectedSubCategoryIds: Array.isArray(req.selectedSubCategoryIds)
            ? req.selectedSubCategoryIds.map((x) => String(x))
            : [],
          requestWorkflowStatus: String(req.requestWorkflowStatus || "").trim(),
          biddingPublished: Boolean(req.biddingPublished),
          affordabilityPrice: toFiniteNumber(req.affordabilityPrice),
        });
      })
      .catch(() => {
        if (!cancelled) setMatchRequestProcedureHydration(null);
      });
    return () => {
      cancelled = true;
    };
  }, [routeMatchRequestIdFromState, activeMatchRequestId, initialMatchRequestId]);

  const selectionListRaw = useMemo(() => getSelectionList(selections), [selections]);

  const matchRequestExpandedBlock = useMemo(() => {
    const rows = safeArray(matchRequestProcedureHydration?.selectionsExpanded);
    return rows[0] || null;
  }, [matchRequestProcedureHydration]);

  const mrLabelParts = useMemo(
    () => splitMatchRequestCategoryLabel(matchRequestProcedureHydration?.categoryLabel),
    [matchRequestProcedureHydration?.categoryLabel]
  );

  const selectionList = useMemo(() => {
    if (selectionListRaw.length) return selectionListRaw;
    const block = matchRequestExpandedBlock;
    if (
      block &&
      (pickId(block.categoryId) ||
        pickName(block.categoryName) ||
        safeArray(block.subCategories).length)
    ) {
      return [block];
    }
    const label = String(matchRequestProcedureHydration?.categoryLabel || "").trim();
    const subIds = matchRequestProcedureHydration?.selectedSubCategoryIds || [];
    if (label && subIds.length) {
      const { category, sub } = splitMatchRequestCategoryLabel(label);
      return [
        {
          categoryName: category,
          subCategoryName: sub,
          subCategories: [
            {
              subCategoryId: subIds[0],
              subCategoryName: sub || category,
            },
          ],
        },
      ];
    }
    return [];
  }, [selectionListRaw, matchRequestExpandedBlock, matchRequestProcedureHydration]);
  const routeSelectedCategory = location?.state?.selectedCategory || null;
  const routeSelectedSubCategory = location?.state?.selectedSubCategory || null;

  const routeCategoryId = pickId(
    routeSelectedCategory?._id,
    routeSelectedCategory?.id,
    routeSelectedCategory?.categoryId
  );
  const routeSubCategoryId = pickId(
    routeSelectedSubCategory?._id,
    routeSelectedSubCategory?.id,
    routeSelectedSubCategory?.subCategoryId
  );

  const selectedSelection = useMemo(() => {
    if (!selectionList.length) return null;

    if (routeSubCategoryId) {
      const bySubCategory = selectionList.find(
        (item) =>
          String(getSelectionSubCategoryId(item) || "") ===
          String(routeSubCategoryId)
      );
      if (bySubCategory) return bySubCategory;
    }

    if (routeCategoryId) {
      const byCategory = selectionList.find(
        (item) =>
          String(getSelectionCategoryId(item) || "") === String(routeCategoryId)
      );
      if (byCategory) return byCategory;
    }

    return selectionList[selectionList.length - 1] || selectionList[0] || null;
  }, [selectionList, routeCategoryId, routeSubCategoryId]);

  const selectedCategory =
    routeSelectedCategory || getSelectedCategory(selectedSelection);
  const selectedSubCategory =
    routeSelectedSubCategory || getSelectedSubCategory(selectedSelection);

  const selectedCategoryId = pickId(
    routeCategoryId,
    selectedCategory?._id,
    selectedCategory?.id,
    selectedSelection?.categoryId?._id,
    selectedSelection?.categoryId?.id,
    selectedSelection?.categoryId,
    matchRequestExpandedBlock?.categoryId
  );

  const selectedSubCategoryId = pickId(
    restoredSubCategoryId,
    routeSubCategoryId,
    selectedSubCategory?._id,
    selectedSubCategory?.id,
    /** Expanded selections from GET /selections use { subCategoryId, subCategoryName } */
    selectedSubCategory?.subCategoryId,
    selectedSelection?.subCategoryId,
    safeArray(selectedSelection?.subCategoryIds)?.[0]?._id,
    safeArray(selectedSelection?.subCategoryIds)?.[0]?.id,
    safeArray(selectedSelection?.subCategoryIds)?.[0],
    safeArray(matchRequestExpandedBlock?.subCategories)?.[0]?.subCategoryId,
    matchRequestProcedureHydration?.selectedSubCategoryIds?.[0]
  );
  const selectedSubCategoryIds = useMemo(() => {
    const fromSelection = getSelectionSubCategoryIds(selectedSelection);
    const fromRequest = safeArray(matchRequestProcedureHydration?.selectedSubCategoryIds)
      .map((x) => String(x))
      .filter(Boolean);
    const leaf = String(selectedSubCategoryId || "").trim();
    const merged = [...fromSelection, ...fromRequest, ...(leaf ? [leaf] : [])].filter(Boolean);
    return Array.from(new Set(merged));
  }, [selectedSelection, matchRequestProcedureHydration, selectedSubCategoryId]);

  const expandedFirstSub = useMemo(
    () => safeArray(selectedSelection?.subCategories)[0] || null,
    [selectedSelection]
  );

  /** Labels from route, nested objects, or GET /selections expanded flat shape. */
  const displayCategoryLabel = useMemo(
    () =>
      pickName(
        routeSelectedCategory?.name,
        selectedCategory?.name,
        selectedSelection?.categoryName,
        matchRequestExpandedBlock?.categoryName,
        mrLabelParts.category
      ),
    [
      routeSelectedCategory?.name,
      selectedCategory?.name,
      selectedSelection?.categoryName,
      matchRequestExpandedBlock?.categoryName,
      mrLabelParts.category,
    ]
  );

  const displaySubCategoryLabel = useMemo(
    () =>
      pickName(
        routeSelectedSubCategory?.name,
        routeSelectedSubCategory?.subCategoryName,
        selectedSubCategory?.name,
        selectedSubCategory?.subCategoryName,
        expandedFirstSub?.subCategoryName,
        expandedFirstSub?.name,
        selectedSelection?.subCategoryName,
        selectedSelection?.subcategoryName,
        safeArray(matchRequestExpandedBlock?.subCategories)[0]?.subCategoryName,
        mrLabelParts.sub
      ),
    [
      routeSelectedSubCategory?.name,
      routeSelectedSubCategory?.subCategoryName,
      selectedSubCategory?.name,
      selectedSubCategory?.subCategoryName,
      expandedFirstSub?.subCategoryName,
      expandedFirstSub?.name,
      selectedSelection?.subCategoryName,
      selectedSelection?.subcategoryName,
      matchRequestExpandedBlock?.subCategories,
      mrLabelParts.sub,
    ]
  );

  /** Full L1→…→L5 path when match-request breadcrumb is present; else shallow labels from /selections. */
  const procedureDisplayParts = useMemo(() => {
    const fromMr = procedurePathPartsFromRequestLike(matchRequestProcedureHydration);
    const fromSel = selectedSelection
      ? procedurePathPartsFromRequestLike({
          selectionsExpanded: [selectedSelection],
          categoryLabel: "",
          categoryBreadcrumb: "",
        })
      : [];
    if (fromMr.length >= fromSel.length && fromMr.length) return fromMr;
    if (fromSel.length) return fromSel;
    return fromMr;
  }, [matchRequestProcedureHydration, selectedSelection]);

  const procedureSummaryReady = Boolean(selectedCategoryId && selectedSubCategoryId);

  const showProcedureSummaryRow =
    procedureDisplayParts.length > 0 ||
    procedureSummaryReady ||
    Boolean(displayCategoryLabel && displaySubCategoryLabel) ||
    Boolean(displayCategoryLabel || displaySubCategoryLabel);

  const procedureLeafLabel =
    procedureDisplayParts.length > 0
      ? procedureDisplayParts[procedureDisplayParts.length - 1]
      : pickName(displaySubCategoryLabel, displayCategoryLabel);

  const activeMrIdForProcedureDisplay = String(
    routeMatchRequestIdFromState || activeMatchRequestId || initialMatchRequestId || ""
  ).trim();

  const isLoadingProcedureFromMatchRequest =
    Boolean(activeMrIdForProcedureDisplay) &&
    selectionListRaw.length === 0 &&
    matchRequestProcedureHydration === undefined;

  /**
   * Match-request patients often see labels/path before Redux ids or `procedureDisplayParts` hydrate.
   */
  const wizardProcedureSatisfied =
    procedureSummaryReady ||
    (!isLoadingProcedureFromMatchRequest &&
      (procedureDisplayParts.length > 0 ||
        Boolean(String(displaySubCategoryLabel || "").trim()) ||
        Boolean(String(displayCategoryLabel || "").trim())));

  const previewList = useMemo(
    () => normalizePreviewList(previewProviders),
    [previewProviders]
  );

  const normalizedMatches = useMemo(() => normalizeMatches(matches), [matches]);

  const [stableMatches, setStableMatches] = useState([]);
  const [stableBidSignature, setStableBidSignature] = useState(null);

  const makeReverseBidSignature = (list) => {
    const safe = Array.isArray(list) ? list : [];
    // Sort to avoid “same data, different order” churn from polling.
    const sorted = [...safe].sort((a, b) => {
      const ak = String(a?.bidId || a?.providerId || a?.id || "");
      const bk = String(b?.bidId || b?.providerId || b?.id || "");
      return ak.localeCompare(bk);
    });

    return JSON.stringify(
      sorted.map((m) => ({
        bidId: m?.bidId,
        providerAction: m?.providerAction,
        reverseBidAmount: m?.price,
      }))
    );
  };

  // Keep showing the last known non-empty provider list to avoid UI vanishing
  // when the backend temporarily returns empty arrays.
  useEffect(() => {
    if (!requestSubmitted) {
      setStableMatches([]);
      setStableBidSignature(null);
      return;
    }
    // New request => reset stable list
    setStableMatches([]);
    setStableBidSignature(null);
  }, [activeMatchRequestId, requestSubmitted]);

  useEffect(() => {
    if (!requestSubmitted) return;

    // Only update stable list when reverse-bid-related data actually changes.
    if (normalizedMatches.length > 0) {
      const currentSignature = makeReverseBidSignature(normalizedMatches);
      if (stableBidSignature === null || currentSignature !== stableBidSignature) {
        setStableBidSignature(currentSignature);
        setStableMatches(matches);
      }
      return;
    }

    // Do not clear stable list on temporary empty responses.
    // (If you start a new request, stable list is reset in the effect above.)
  }, [normalizedMatches, matches, requestSubmitted, stableBidSignature]);

  const normalizedStableMatches = useMemo(
    () => normalizeMatches(stableMatches),
    [stableMatches]
  );

  const displayedList =
    requestSubmitted
      ? normalizedStableMatches.length > 0
        ? normalizedStableMatches
        : normalizedMatches.length > 0
          ? normalizedMatches
          : normalizedStableMatches
      : previewList;
  /** Post-submit / live-bidding UI only — never infer from Redux `matchRequest.message` (stale / ambiguous). */
  const isSubmitted = requestSubmitted;
  /** Only after bidding is live or user submitted from this page — not merely because an MR id exists (e.g. VERIFIED). */
  const hasActiveSubmittedRequest = requestSubmitted;

  const existingMrWorkflow = String(
    matchRequestProcedureHydration?.requestWorkflowStatus || ""
  ).toUpperCase();
  /** PENDING: allow Save budget (PATCH). VERIFIED: Notify doctors (open-bidding). */
  const isPendingAdminReview = existingMrWorkflow === "PENDING_ADMIN_REVIEW";
  const isVerifiedReadyToNotify = existingMrWorkflow === "VERIFIED";

  const hasPatientCoordsForZone =
    Boolean(patientCoords.patientLat && patientCoords.patientLng) &&
    Number.isFinite(Number(patientCoords.patientLat)) &&
    Number.isFinite(Number(patientCoords.patientLng));

  /** Within Zone limits preview to a radius from GPS; 0 matches often means distance, not wrong specialty. */
  const showWithinZoneZeroPreviewHelp =
    !isSubmitted &&
    locationScope === "withinZone" &&
    hasPatientCoordsForZone &&
    Boolean(selectedCategoryId && selectedSubCategoryId) &&
    displayedList.length === 0;

  const submissionStorageKey = useMemo(() => {
    const subId = String(selectedSubCategoryId || "");
    const scope = String(locationScope || "");
    const pin = scope === "locationWise" ? String(searchPincode || "").trim() : "";
    // withinZone coords vary; we intentionally do NOT include them in key
    return `healbid:careprice:submitted:${subId}:${scope}:${pin}`;
  }, [selectedSubCategoryId, locationScope, searchPincode]);

  // Sync MR id from the localStorage key for the *current* procedure + location scope.
  // Declared *before* the LAST-id restore effect: on the same tick this effect may clear the id when
  // there is no per-key entry; the LAST effect runs next and restores `activeMatchRequestId` from
  // `healbid:careprice:lastMatchRequestId` (later setState wins in the batch).
  // Deps must NOT include `activeMatchRequestId` / `requestSubmitted`: restoring scope/subId updates
  // `submissionStorageKey` and used to retrigger this effect in a loop (detail + provider-count + matches).
  useEffect(() => {
    const routedMr = String(
      routeMatchRequestIdFromState ||
        (initialMatchRequestId != null && initialMatchRequestId !== ""
          ? String(initialMatchRequestId)
          : "")
    ).trim();
    // Request review / detail opens `/care-price` with `state.matchRequestId` (or Gate passes `initialMatchRequestId`).
    // A stale `healbid:careprice:submitted:*` entry for the same procedure + area would otherwise overwrite
    // that id in this effect and flash the wrong "live bid" screen after "Place bid".
    if (routedMr) {
      if (submissionStorageKey) {
        try {
          const saved = window.localStorage.getItem(submissionStorageKey);
          const sTrim = saved && String(saved).trim();
          if (sTrim && sTrim !== routedMr) {
            window.localStorage.removeItem(submissionStorageKey);
          }
        } catch (_) {}
      }
      return undefined;
    }

    dispatch(clearMatchResults());
    setLocationPermissionError("");

    const savedId = submissionStorageKey ? window.localStorage.getItem(submissionStorageKey) : "";
    if (savedId && String(savedId).trim()) {
      const rid = String(savedId).trim();
      setActiveMatchRequestId(rid);
      let cancelled = false;
      (async () => {
        try {
          const detailRes = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(rid));
          const row = pickMatchRequestRowFromDetailResponse(detailRes);
          const w = String(row?.requestWorkflowStatus || "").toUpperCase();
          const published = Boolean(row?.biddingPublished);
          const biddingLive =
            published || ["BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(w);
          if (cancelled) return;
          if (biddingLive) {
            setRequestSubmitted(true);
            dispatch(getProviderCount(rid));
            dispatch(getMatchResults(rid));
          } else {
            setRequestSubmitted(false);
            dispatch(clearMatchResults());
          }
        } catch {
          if (!cancelled) {
            setRequestSubmitted(false);
            dispatch(clearMatchResults());
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setActiveMatchRequestId("");
    setRequestSubmitted(false);
    return undefined;
  }, [
    submissionStorageKey,
    dispatch,
    LAST_MATCH_REQUEST_ID_KEY,
    routeMatchRequestIdFromState,
    initialMatchRequestId,
  ]);

  // Restore active submitted match request after logout/login.
  // This should NOT depend on locationScope/searchPincode because those can change on refresh/login.
  useEffect(() => {
    const explicitResumeMr = String(
      routeMatchRequestIdFromState || (initialMatchRequestId != null ? String(initialMatchRequestId) : "") || ""
    ).trim();
    // Opening /care-price from the app shell without a specific request should not silently
    // reattach the last bidding session (felt like an automatic bid + hid the full flow).
    if (!explicitResumeMr) return;

    if (requestSubmitted) return;
    if (activeMatchRequestId) return;
    const savedId = (() => {
      try {
        const direct = window.localStorage.getItem(LAST_MATCH_REQUEST_ID_KEY);
        if (direct && String(direct).trim()) return direct;

        // Backward compatible fallback:
        // older versions stored requestId inside location/category dependent keys
        // like: healbid:careprice:submitted:<subId>:<scope>:<pin>
        const keys = Object.keys(window.localStorage || {});
        let found = null;
        for (const k of keys) {
          if (!k.startsWith("healbid:careprice:submitted:")) continue;
          const v = window.localStorage.getItem(k);
          if (v && String(v).trim()) found = v; // keep last match we find
        }
        return found;
      } catch (_) {
        return null;
      }
    })();

    if (!savedId || !String(savedId).trim()) return;

    const requestId = String(savedId).trim();
    const routeIntentSub = String(routeSubCategoryId || "").trim();

    let cancelled = false;
    (async () => {
      try {
        const detailRes = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(requestId));
        const row = pickMatchRequestRowFromDetailResponse(detailRes);
        if (cancelled) return;

        // Onboarding / category flow often passes `selectedSubCategory` in route state. If LAST_MR
        // points at a different procedure’s request, do not resume bidding (avoids “auto bid” feel).
        const mrSubs = collectDetailRowSubCategoryIds(row);
        if (
          routeIntentSub &&
          explicitResumeMr !== String(requestId) &&
          row &&
          mrSubs.length > 0 &&
          !mrSubs.includes(routeIntentSub)
        ) {
          try {
            if (window.localStorage.getItem(LAST_MATCH_REQUEST_ID_KEY) === requestId) {
              window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
            }
          } catch (_) {}
          if (!cancelled) {
            setActiveMatchRequestId("");
            setRequestSubmitted(false);
            dispatch(clearMatchResults());
          }
          return;
        }

        setActiveMatchRequestId(requestId);

        const w = String(row?.requestWorkflowStatus || "").toUpperCase();
        const published = Boolean(row?.biddingPublished);
        const biddingLive =
          published || ["BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(w);
        if (biddingLive) {
          setRequestSubmitted(true);
          dispatch(getProviderCount(requestId));
          dispatch(getMatchResults(requestId));
        } else {
          setRequestSubmitted(false);
          dispatch(clearMatchResults());
        }

        // Restore scope + subCategoryId too, so the price slider can fetch lowest price.
        // Key format (created in UI on submit):
        //   healbid:careprice:submitted:<subId>:<scope>:<pin>
        try {
          const submittedPrefix = "healbid:careprice:submitted:";
          const keys = Object.keys(window.localStorage || {});
          let matchedKey = null;

          for (const k of keys) {
            if (!k.startsWith(submittedPrefix)) continue;
            const v = window.localStorage.getItem(k);
            if (v && String(v).trim() === requestId) {
              matchedKey = k; // keep last match
            }
          }

          if (matchedKey) {
            const rest = matchedKey.slice(submittedPrefix.length); // <subId>:<scope>:<pin>
            const parts = rest.split(":");
            const subId = parts[0] ? String(parts[0]) : "";
            const scope = parts[1] ? String(parts[1]) : "";
            const pin = parts.slice(2).join(":"); // pin can be empty string

            if (subId && subId.trim()) setRestoredSubCategoryId(subId.trim());

            if (scope) {
              if (scope === "withinZone" || scope === "allOverWestBengal" || scope === "locationWise") {
                setLocationScope(scope);
              }
            }

            if (scope === "locationWise") {
              setSearchPincode((pin || "").replace(/\D/g, "").slice(0, 6));
            }
          }
        } catch (_) {
          // ignore localStorage parse errors
        }

        try {
          window.localStorage.setItem(LAST_MATCH_REQUEST_ID_KEY, requestId);
        } catch (_) {}
      } catch {
        if (!cancelled) {
          setRequestSubmitted(false);
          dispatch(clearMatchResults());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    LAST_MATCH_REQUEST_ID_KEY,
    requestSubmitted,
    activeMatchRequestId,
    routeSubCategoryId,
    routeMatchRequestIdFromState,
    initialMatchRequestId,
  ]);

  // If `activeMatchRequestId` was restored before route `selectedSubCategory` arrived, drop the old
  // request once we can prove it belongs to another procedure than the one in navigation state.
  useEffect(() => {
    const rid = String(activeMatchRequestId || "").trim();
    const intent = String(routeSubCategoryId || "").trim();
    const explicitMr = String(routeMatchRequestIdFromState || "").trim();
    if (!rid || !intent) return;
    if (explicitMr && explicitMr === rid) return;

    let cancelled = false;
    (async () => {
      try {
        const detailRes = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(rid));
        const row = pickMatchRequestRowFromDetailResponse(detailRes);
        const mrSubs = collectDetailRowSubCategoryIds(row);
        if (!mrSubs.length || mrSubs.includes(intent) || cancelled) return;

        try {
          if (window.localStorage.getItem(LAST_MATCH_REQUEST_ID_KEY) === rid) {
            window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
          }
          const keys = Object.keys(window.localStorage || {});
          keys.forEach((k) => {
            if (k.startsWith("healbid:careprice:submitted:") && window.localStorage.getItem(k) === rid) {
              window.localStorage.removeItem(k);
            }
          });
        } catch (_) {}
        setActiveMatchRequestId("");
        setRequestSubmitted(false);
        setRestoredSubCategoryId(null);
        dispatch(clearMatchResults());
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMatchRequestId, routeSubCategoryId, routeMatchRequestIdFromState, dispatch, LAST_MATCH_REQUEST_ID_KEY]);

  // When match request fetch fails (404 / not found), clear stale state so new users or new sessions can submit again.
  useEffect(() => {
    if (!activeMatchRequestId || !requestSubmitted) return;
    const errMsg = typeof error === "string" ? error : "";
    if (!errMsg || !/not found|Match request not found|404/i.test(errMsg)) return;

    const requestId = String(activeMatchRequestId).trim();
    setActiveMatchRequestId("");
    setRequestSubmitted(false);
    try {
      if (window.localStorage.getItem(LAST_MATCH_REQUEST_ID_KEY) === requestId) {
        window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
      }
      const keys = Object.keys(window.localStorage || {});
      keys.forEach((k) => {
        if (k.startsWith("healbid:careprice:submitted:") && window.localStorage.getItem(k) === requestId) {
          window.localStorage.removeItem(k);
        }
      });
    } catch (_) {}
    dispatch(clearMatchResults());
  }, [error, activeMatchRequestId, requestSubmitted, dispatch, LAST_MATCH_REQUEST_ID_KEY]);

  useEffect(() => {
    dispatch(getSelections());

    return () => {
      dispatch(clearPatientFlowError());
      dispatch(clearPatientFlowSuccess());
      dispatch(clearMatchResults());
      dispatch(clearPreviewProviders());
    };
  }, [dispatch]);

  useEffect(() => {
    const prefetched = location?.state?.prefetchedPriceInfo;
    if (!prefetched) return;

    const normalized = normalizePriceInfo(prefetched);
    setPriceLimits({
      min: normalized.lowestPrice,
      max: normalized.highestPrice,
      initial: normalized.initialBidAmount,
    });
    setPrice(
      snapBidToSliderStep(
        normalized.initialBidAmount,
        normalized.lowestPrice,
        normalized.highestPrice
      )
    );
  }, [location?.state?.prefetchedPriceInfo]);

  useEffect(() => {
    if (!selectedSubCategoryId) return;
    dispatch(getLowestPrice({ subCategoryId: selectedSubCategoryId }));
  }, [dispatch, selectedSubCategoryId]);

  // Always derive slider bounds: when priceInfo is still null, use ₹0–₹30L fallback so UI is never stuck on "Loading price range...".
  useEffect(() => {
    const subId = String(selectedSubCategoryId || "").trim();
    if (!subId) {
      if (!initialMatchRequestId) return;
      const normalized = normalizePriceInfo({});
      setPriceLimits({
        min: normalized.lowestPrice,
        max: normalized.highestPrice,
        initial: normalized.initialBidAmount,
      });
      if (!requestSubmitted) {
        setPrice(
          snapBidToSliderStep(
            normalized.initialBidAmount,
            normalized.lowestPrice,
            normalized.highestPrice
          )
        );
      }
      return;
    }

    if (
      priceInfo?.requestedSubCategoryId &&
      String(priceInfo.requestedSubCategoryId) !== subId
    ) {
      const normalized = normalizePriceInfo({});
      setPriceLimits({
        min: normalized.lowestPrice,
        max: normalized.highestPrice,
        initial: normalized.initialBidAmount,
      });
      if (!requestSubmitted) {
        setPrice(
          snapBidToSliderStep(
            normalized.initialBidAmount,
            normalized.lowestPrice,
            normalized.highestPrice
          )
        );
      }
      return;
    }

    const normalized = normalizePriceInfo(priceInfo || {});
    setPriceLimits({
      min: normalized.lowestPrice,
      max: normalized.highestPrice,
      initial: normalized.initialBidAmount,
    });
    if (!requestSubmitted) {
      setPrice(
        snapBidToSliderStep(
          normalized.initialBidAmount,
          normalized.lowestPrice,
          normalized.highestPrice
        )
      );
    }
  }, [priceInfo, selectedSubCategoryId, initialMatchRequestId, requestSubmitted]);

  const requestCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationPermissionError("Geolocation is not supported in this browser.");
      return;
    }
    if (geoLocateInFlightRef.current) return;
    geoLocateInFlightRef.current = true;

    setLocationPermissionError("");
    setLocationRequesting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const nextCoords = {
            patientLat: String(lat),
            patientLng: String(lng),
          };
          setPatientCoords({
            patientLat: nextCoords.patientLat,
            patientLng: nextCoords.patientLng,
          });
          setGeoPermissionState("granted");
          try {
            window.localStorage.setItem(LAST_PATIENT_COORDS_KEY, JSON.stringify(nextCoords));
          } catch (_) {}
          try {
            await dispatch(savePatientLocation({ latitude: lat, longitude: lng })).unwrap();
          } catch (e) {
            const msg = String(e || "").trim();
            if (msg) setLocationPermissionError(msg);
          }
        } finally {
          geoLocateInFlightRef.current = false;
          setLocationRequesting(false);
        }
      },
      (geoError) => {
        const code = Number(geoError?.code);
        if (code === 1) {
          setGeoPermissionState("denied");
          setLocationPermissionError(
            "Location permission is blocked. Enable it in browser/app settings and try again."
          );
        } else if (code === 2) {
          setLocationPermissionError("Unable to detect your location. Please retry.");
        } else if (code === 3) {
          setLocationPermissionError("Location request timed out. Please retry.");
        } else {
          setLocationPermissionError("Location permission is required for Within Zone.");
        }
        geoLocateInFlightRef.current = false;
        setLocationRequesting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [dispatch]);

  useEffect(() => {
    let live = true;
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return undefined;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (!live) return;
        setGeoPermissionState(String(status.state || "prompt"));
        status.onchange = () => setGeoPermissionState(String(status.state || "prompt"));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const lat = Number(patientLocation?.latitude);
    const lng = Number(patientLocation?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setPatientCoords({ patientLat: String(lat), patientLng: String(lng) });
  }, [patientLocation?.latitude, patientLocation?.longitude]);

  useEffect(() => {
    if (patientCoords.patientLat && patientCoords.patientLng) return;
    try {
      const raw = window.localStorage.getItem(LAST_PATIENT_COORDS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const lat = Number(parsed?.patientLat);
      const lng = Number(parsed?.patientLng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setPatientCoords({ patientLat: String(lat), patientLng: String(lng) });
    } catch (_) {}
  }, [patientCoords.patientLat, patientCoords.patientLng]);

  /**
   * Near me: call geolocation whenever coords are missing — including on first paint, after
   * each deny, and when the user taps Near me again (coords are cleared on that card).
   * Browsers may not show a repeated system prompt after a hard deny; we still call
   * getCurrentPosition so a later allow (or settings change) can succeed without a manual button.
   */
  useEffect(() => {
    if (locationScope !== "withinZone") {
      geoLocateInFlightRef.current = false;
      return undefined;
    }
    if (!navigator.geolocation) {
      setLocationPermissionError("Geolocation is not supported in this browser.");
      return undefined;
    }
    if (patientCoords.patientLat && patientCoords.patientLng) return undefined;

    requestCurrentLocation();
    return undefined;
  }, [
    locationScope,
    patientCoords.patientLat,
    patientCoords.patientLng,
    geoPermissionState,
    requestCurrentLocation,
  ]);

  useEffect(() => {
    if (!isPriceSliding) return undefined;

    const stopSliding = () => setIsPriceSliding(false);
    window.addEventListener("mouseup", stopSliding);
    window.addEventListener("touchend", stopSliding);
    window.addEventListener("touchcancel", stopSliding);

    return () => {
      window.removeEventListener("mouseup", stopSliding);
      window.removeEventListener("touchend", stopSliding);
      window.removeEventListener("touchcancel", stopSliding);
    };
  }, [isPriceSliding]);

  useEffect(() => {
    if (!selectedCategoryId || !selectedSubCategoryId) {
      dispatch(clearPreviewProviders());
      return;
    }

    if (
      locationScope === "withinZone" &&
      (!patientCoords.patientLat || !patientCoords.patientLng)
    ) {
      dispatch(clearPreviewProviders());
      return;
    }

    if (locationScope === "locationWise") {
      if (!searchPincode.trim()) {
        dispatch(clearPreviewProviders());
        return;
      }
      if (previewSearchPincode !== searchPincode.trim()) {
        return;
      }
    }

    const payload = {
      locationScope,
      patientLat:
        locationScope === "withinZone" ? Number(patientCoords.patientLat) : undefined,
      patientLng:
        locationScope === "withinZone" ? Number(patientCoords.patientLng) : undefined,
      searchPincode:
        locationScope === "locationWise" ? previewSearchPincode : undefined,
      searchPlaceId: undefined,
      ...(locationScope === "withinZone" ? { withinZoneRadiusKm: previewWithinZoneRadiusKm } : {}),
      // Backend filters providers by sub-category; send page selection so preview shows matching doctors/hospitals
      selections: [
        {
          categoryId: selectedCategoryId,
          subCategoryIds: selectedSubCategoryIds.length
            ? selectedSubCategoryIds
            : [selectedSubCategoryId],
        },
      ],
    };

    dispatch(getPreviewProviders(payload));
  }, [
    dispatch,
    selectedCategoryId,
    selectedSubCategoryId,
    selectedSubCategoryIds,
    locationScope,
    patientCoords.patientLat,
    patientCoords.patientLng,
    searchPincode,
    previewSearchPincode,
    previewWithinZoneRadiusKm,
  ]);

  useEffect(() => {
    // Initial fetch for the active submitted request. Live updates come from Socket.IO.
    // Avoid putting `matchRequest` in deps, because Redux updates can re-run
    // this effect and make the UI feel like it's "refreshing constantly".
    const requestId = String(activeMatchRequestId || "").trim();
    if (!requestSubmitted || !requestId) return;

    dispatch(getProviderCount(requestId));
    dispatch(getMatchResults(requestId));
  }, [dispatch, requestSubmitted, activeMatchRequestId]);

  // Socket.IO live bids. REST-poll only while the socket is missing or disconnected.
  useEffect(() => {
    const requestId = String(activeMatchRequestId || "").trim();
    if (!requestSubmitted || !requestId) return;

    let pollId = null;
    const refresh = () => {
      try {
        dispatch(getMatchResults(requestId));
        dispatch(getProviderCount(requestId));
      } catch (_) {}
    };
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(refresh, 8000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: "patient" });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }
    socketRef.current = socket;

    const onBidUpdate = (payload) => {
      try {
        if (!payload) return;
        if (String(payload.matchRequestId || "") !== requestId) return;
        const t = String(payload.type || "").toLowerCase();
        if (t === "chat_message" || t === "conversation_activity") return;
        refresh();
      } catch (_) {}
    };

    socket.on("bid:update", onBidUpdate);
    socket.on("notification:new", onBidUpdate);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();

    return () => {
      try {
        socket.off("bid:update", onBidUpdate);
        socket.off("notification:new", onBidUpdate);
        socket.off("connect", stopPolling);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [dispatch, requestSubmitted, activeMatchRequestId]);

  const resetToAverage = () => {
    if (!priceLimits) return;
    setPrice(snapBidToSliderStep(priceLimits.initial, priceLimits.min, priceLimits.max));
    setRequestSubmitted(false);
    setCareGlimpseMode(false);
    setActiveMatchRequestId("");
    setSubmitRequestError("");
    try {
      window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
    } catch (_) {}
    dispatch(clearMatchResults());
  };

  /**
   * Clears the “live bid” UI so the patient can tap Share my budget again. Match requests already shared
   * with providers cannot be edited via PATCH; this starts a new POST match request for the same
   * procedure/location key without resurrecting the saved `healbid:careprice:submitted:*` session.
   */
  const exitLiveBidForNewPlaceBid = useCallback(() => {
    const rid = String(activeMatchRequestId || "").trim();
    dispatch(clearMatchResults());
    setRequestSubmitted(false);
    setCareGlimpseMode(false);
    setActiveMatchRequestId("");
    setStableMatches([]);
    setStableBidSignature(null);
    setSubmitRequestError("");
    try {
      window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
      if (submissionStorageKey) {
        window.localStorage.removeItem(submissionStorageKey);
      }
      if (rid) {
        const keys = Object.keys(window.localStorage || {});
        for (const k of keys) {
          if (k.startsWith("healbid:careprice:submitted:") && window.localStorage.getItem(k) === rid) {
            window.localStorage.removeItem(k);
          }
        }
      }
    } catch (_) {}
  }, [dispatch, submissionStorageKey, activeMatchRequestId, LAST_MATCH_REQUEST_ID_KEY]);

  /** Creates the patient match request (POST /app/match-requests) only after location + budget are set here. */
  const handleSubmitRequest = async () => {
    if (!selectedCategoryId || !selectedSubCategoryId) return;

    if (
      locationScope === "withinZone" &&
      (!patientCoords.patientLat || !patientCoords.patientLng)
    ) {
      setLocationPermissionError("Please allow location access for Within Zone.");
      return;
    }

    if (locationScope === "locationWise" && !searchPincode.trim()) {
      return;
    }

    setSubmitRequestError("");
    dispatch(clearMatchResults());

    const bidAmount =
      priceBoundsReady && priceLimits
        ? snapBidToSliderStep(price, priceLimits.min, priceLimits.max)
        : price;

    const payload = {
      affordabilityPrice: String(bidAmount),
      maxBudget: String(bidAmount),
      locationScope,
      packageScope: {
        procedureOt: packageProcedureOt,
        implants: packageImplants,
        hospitalStay: packageHospitalStay,
      },
      patientLat:
        locationScope === "withinZone" ? String(patientCoords.patientLat) : undefined,
      patientLng:
        locationScope === "withinZone" ? String(patientCoords.patientLng) : undefined,
      searchPincode:
        locationScope === "locationWise" ? searchPincode.trim() : undefined,
      searchPlaceId: undefined,
      ...(locationScope === "withinZone" ? { withinZoneRadiusKm } : {}),
      selections: [
        {
          categoryId: selectedCategoryId,
          subCategoryIds: selectedSubCategoryIds.length
            ? selectedSubCategoryIds
            : [selectedSubCategoryId],
        },
      ],
    };

    setIsSubmittingRequest(true);
    try {
      const mrIdForSubmit = String(activeMatchRequestId || routeMatchRequestIdFromState || "").trim();
      const wfUp = String(existingMrWorkflow || "").toUpperCase();

      if (mrIdForSubmit && wfUp === "") {
        setSubmitRequestError("Still loading your request status. Please wait a moment and try again.");
        return;
      }

      if (mrIdForSubmit && wfUp && wfUp !== "VERIFIED" && wfUp !== "PENDING_ADMIN_REVIEW") {
        setSubmitRequestError(
          "This care request cannot be sent from this screen. Open it from your dashboard or requests list."
        );
        return;
      }

      const openVerifiedExisting = Boolean(mrIdForSubmit && wfUp === "VERIFIED" && !requestSubmitted);
      const savePendingDraft = Boolean(mrIdForSubmit && wfUp === "PENDING_ADMIN_REVIEW");

      if (savePendingDraft) {
        const patchBody = {
          affordabilityPrice: String(bidAmount),
          maxBudget: String(bidAmount),
          locationScope,
          packageScope: {
            procedureOt: packageProcedureOt,
            implants: packageImplants,
            hospitalStay: packageHospitalStay,
          },
          ...(locationScope === "withinZone"
            ? {
                patientLat: String(patientCoords.patientLat),
                patientLng: String(patientCoords.patientLng),
                withinZoneRadiusKm,
              }
            : {}),
          ...(locationScope === "locationWise" ? { searchPincode: searchPincode.trim() } : {}),
          selections: payload.selections,
        };
        await API.patch(ENDPOINTS.UPDATE_MATCH_REQUEST(mrIdForSubmit), patchBody);
        setSubmitRequestError("");
        setRequestSubmitted(false);
        window.alert(
          "Budget saved. Doctors will be notified after admin approves your documents and you confirm."
        );
        return;
      }

      if (openVerifiedExisting) {
        const patchBody = {
          affordabilityPrice: String(bidAmount),
          maxBudget: String(bidAmount),
          locationScope,
          packageScope: {
            procedureOt: packageProcedureOt,
            implants: packageImplants,
            hospitalStay: packageHospitalStay,
          },
          ...(locationScope === "withinZone"
            ? {
                patientLat: String(patientCoords.patientLat),
                patientLng: String(patientCoords.patientLng),
                withinZoneRadiusKm,
              }
            : {}),
          ...(locationScope === "locationWise" ? { searchPincode: searchPincode.trim() } : {}),
          selections: payload.selections,
        };
        await API.patch(ENDPOINTS.UPDATE_MATCH_REQUEST(mrIdForSubmit), patchBody);
        const openRes = await API.post(ENDPOINTS.OPEN_MATCH_REQUEST_BIDDING(mrIdForSubmit), {
          confirmOpenBidding: true,
        });
        const openPayload = openRes?.data ?? openRes ?? {};
        const requestIdStr = String(
          pickId(
            openPayload?.matchRequestId,
            openPayload?._id,
            openPayload?.id,
            mrIdForSubmit
          ) || mrIdForSubmit
        );

        setRequestSubmitted(true);
        setActiveMatchRequestId(requestIdStr);
        setSubmitRequestError("");
        if (submissionStorageKey) {
          try {
            window.localStorage.setItem(submissionStorageKey, requestIdStr);
          } catch (_) {}
        }
        try {
          window.localStorage.setItem(LAST_MATCH_REQUEST_ID_KEY, requestIdStr);
        } catch (_) {}

        dispatch(clearPatientFlowSuccess());
        navigate("/budget-shared-success", {
          replace: true,
          state: { matchRequestId: requestIdStr },
        });
        return;
      }

      const actionResult = await dispatch(createMatchRequest(payload));

      if (createMatchRequest.fulfilled.match(actionResult)) {
        const resPayload = actionResult.payload || {};
        const requestId = pickId(
          resPayload?.matchRequestId,
          resPayload?._id,
          resPayload?.id,
          resPayload?.data?.matchRequestId,
          resPayload?.data?._id,
          resPayload?.data?.id,
          resPayload?.matchRequest?._id,
          resPayload?.matchRequest?.id,
          resPayload?.matchRequest?.matchRequestId,
          resPayload?.data?.matchRequest?._id,
          resPayload?.data?.matchRequest?.id,
          resPayload?.data?.matchRequest?.matchRequestId
        );

        if (requestId) {
          const requestIdStr = String(requestId);
          const biddingPublished = Boolean(resPayload.biddingPublished);
          const wf = String(resPayload.requestWorkflowStatus || "").toUpperCase();
          const biddingOpened = biddingPublished || wf === "BIDDING";

          setRequestSubmitted(biddingOpened);
          setActiveMatchRequestId(requestIdStr);
          setSubmitRequestError(
            biddingOpened
              ? ""
              : "Request saved. When every linked document is approved, open your care request review and tap “Continue to bids & pricing” to send your offer to doctors."
          );
          if (submissionStorageKey) {
            try {
              window.localStorage.setItem(submissionStorageKey, requestIdStr);
            } catch (_) {}
          }
          try {
            window.localStorage.setItem(LAST_MATCH_REQUEST_ID_KEY, requestIdStr);
          } catch (_) {}

          if (biddingOpened) {
            dispatch(clearPatientFlowSuccess());
            navigate("/budget-shared-success", {
              replace: true,
              state: { matchRequestId: requestIdStr },
            });
            return;
          }
          dispatch(clearMatchResults());
          dispatch(clearPatientFlowSuccess());
        }
      }
    } catch (submitErr) {
      const errMessage = String(
        submitErr?.response?.data?.message ||
          submitErr?.message ||
          "Could not submit your request right now."
      ).trim();
      setRequestSubmitted(false);
      setSubmitRequestError(errMessage);
      console.warn("Submit request flow error", submitErr);
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const submitReverseBidModal = async () => {
    if (!reverseBidModal?.bidId) return;

    const rawAmt = reverseBidModal.amount ?? "";
    const digits = bidAmountDigitsOnly(rawAmt);
    const phoneLike = validateBidAmountDigitsNotPhoneLike(digits);
    if (phoneLike) {
      setReverseBidError(phoneLike);
      return;
    }
    const amount = bidAmountInputToNumber(rawAmt);
    const patientBid = Number(reverseBidModal.patientBid ?? 0);

    if (!Number.isFinite(amount) || amount <= 0) {
      setReverseBidError("Please enter a valid amount.");
      return;
    }

    if (patientBid && Number.isFinite(patientBid) && amount < patientBid) {
      setReverseBidError(
        `Counter amount can't be less than your bid (${fmtINR(patientBid)}).`
      );
      return;
    }

    setCounterSubmitting(true);
    try {
      const actionResult = await dispatch(
        patientCounterBid({ bidId: reverseBidModal.bidId, amount })
      );

      const requestId = pickId(
        activeMatchRequestId,
        matchRequest?.matchRequestId,
        matchRequest?._id,
        matchRequest?.id,
        matchRequest?.matchRequest?._id,
        matchRequest?.matchRequest?.id
      );

      if (patientCounterBid.fulfilled.match(actionResult) && requestId) {
        dispatch(getMatchResults(requestId));
        dispatch(getProviderCount(requestId));
      }

      setReverseBidModal(null);
      setReverseBidError("");
    } finally {
      setCounterSubmitting(false);
    }
  };

  /** Submit does not require preview count > 0; Within Zone needs saved lat/lng (see disabled + hint). */
  const submitRequestDisabled =
    isSubmittingRequest ||
    hasActiveSubmittedRequest ||
    !selectedCategoryId ||
    !selectedSubCategoryId ||
    (locationScope === "withinZone" &&
      (!patientCoords.patientLat || !patientCoords.patientLng)) ||
    (locationScope === "locationWise" && !searchPincode.trim());

  const submitRequestDisabledHint = (() => {
    if (isSubmittingRequest) return "";
    if (hasActiveSubmittedRequest) {
      return "Your budget is live. Open View summary to see matching doctors, or use Edit from there to review your choices.";
    }
    if (!selectedCategoryId || !selectedSubCategoryId) {
      return "Choose your procedure (category and care type) first — use “Change procedure” above, then set location and budget.";
    }
    if (
      locationScope === "withinZone" &&
      (!patientCoords.patientLat || !patientCoords.patientLng)
    ) {
      return "Submit stays off until your browser shares your location for Near me (allow the prompt or enable location in settings).";
    }
    if (locationScope === "locationWise" && !searchPincode.trim()) {
      return "Enter a 6-digit pincode to share your budget.";
    }
    return "";
  })();

  const safeMin = priceBoundsReady ? priceLimits.min : 0;
  const safeMax = priceBoundsReady ? priceLimits.max : 1;
  const safePrice = priceBoundsReady
    ? snapBidToSliderStep(clamp(price, safeMin, safeMax), safeMin, safeMax)
    : 0;

  /** Match request / GET matches root `affordabilityPrice` — source of truth for the patient offer (doctor dashboard uses the same). */
  const liveSubmittedOfferFromServer = useMemo(() => {
    if (!requestSubmitted) return null;
    return (
      toFiniteNumber(matchResultsRaw?.affordabilityPrice) ??
      toFiniteNumber(matchRequestProcedureHydration?.affordabilityPrice) ??
      toFiniteNumber(matchRequest?.affordabilityPrice)
    );
  }, [
    requestSubmitted,
    matchResultsRaw?.affordabilityPrice,
    matchRequestProcedureHydration?.affordabilityPrice,
    matchRequest?.affordabilityPrice,
  ]);

  const glimpseBudgetDisplayAmount = useMemo(() => {
    const server = liveSubmittedOfferFromServer;
    if (server != null && server > 0) {
      if (priceBoundsReady && priceLimits) {
        return snapBidToSliderStep(server, priceLimits.min, priceLimits.max);
      }
      return server;
    }
    return safePrice;
  }, [liveSubmittedOfferFromServer, priceBoundsReady, priceLimits, safePrice]);

  const priceFill = `${
    ((safePrice - safeMin) / Math.max(safeMax - safeMin, 1)) * 100
  }%`;

  useEffect(() => {
    if (!wizardProcedureSatisfied && careWizardStep !== 1) setCareWizardStep(1);
  }, [wizardProcedureSatisfied, careWizardStep]);

  // When a prior session’s match request is already in bidding, keep the mobile wizard on the budget
  // / results step so we don’t show “Step 1 · Continue to location” while polls and notifications run.
  useEffect(() => {
    if (requestSubmitted) setCareWizardStep(3);
  }, [requestSubmitted]);

  useEffect(() => {
    setCareGlimpseMode(Boolean(requestSubmitted));
  }, [requestSubmitted]);

  const previewDocCount = useMemo(
    () => displayedList.filter((d) => String(d?.role).toLowerCase() !== "hospital").length,
    [displayedList]
  );
  const previewHospCount = useMemo(
    () => displayedList.filter((d) => String(d?.role).toLowerCase() === "hospital").length,
    [displayedList]
  );

  const recommendedBand = useMemo(() => {
    if (!priceBoundsReady || !priceLimits) return null;
    const mid = Number(priceLimits.initial) || (priceLimits.min + priceLimits.max) / 2;
    const lo = Math.round(Math.max(priceLimits.min, mid * 0.88));
    const hi = Math.round(Math.min(priceLimits.max, mid * 1.12));
    return { lo, hi: Math.max(lo + 1, hi) };
  }, [priceBoundsReady, priceLimits]);

  const withinSuggestedBudget = Boolean(
    recommendedBand &&
      priceBoundsReady &&
      safePrice >= recommendedBand.lo &&
      safePrice <= recommendedBand.hi
  );

  /** Highlights the suggested ₹ band on the budget track (CSS vars for ::before). */
  const budgetRecTrackStyle = useMemo(() => {
    if (!priceBoundsReady || !priceLimits || !recommendedBand) return undefined;
    const span = Math.max(priceLimits.max - priceLimits.min, 1);
    const lo = clamp(recommendedBand.lo, priceLimits.min, priceLimits.max);
    const hi = clamp(recommendedBand.hi, priceLimits.min, priceLimits.max);
    const loPct = ((lo - priceLimits.min) / span) * 100;
    const hiPct = ((hi - priceLimits.min) / span) * 100;
    return {
      "--cpBudgetRecLoPct": `${loPct}%`,
      "--cpBudgetRecHiPct": `${hiPct}%`,
    };
  }, [priceBoundsReady, priceLimits, recommendedBand]);

  const goalMobileStep = wizardProcedureSatisfied ? careWizardStep : 1;
  const showPostSubmitGlimpse = requestSubmitted && careGlimpseMode;
  /** Narrow: keep procedure card (incl. Change procedure) visible whenever a procedure is set — step 2+ used to hide it. */
  const goalShowProcedure =
    !showPostSubmitGlimpse &&
    (layoutIsWide || goalMobileStep === 1 || wizardProcedureSatisfied);
  /** Narrow: same threshold as budget — step 3 (submitted / restored MR) must still show location, not only step 2. */
  const goalShowLocation =
    !showPostSubmitGlimpse && (layoutIsWide || goalMobileStep >= 2);
  /** Narrow layouts: location + budget share step 2 (no separate “Continue to budget”). */
  const goalShowBudget = !showPostSubmitGlimpse && (layoutIsWide || goalMobileStep >= 2);

  const goalProcedureShort = pickName(
    procedureLeafLabel,
    displaySubCategoryLabel,
    displayCategoryLabel,
    "Procedure"
  );

  /** Live bid summary: show leaf procedure only (no category → sub-category trail). */
  const glimpseProcedureText = useMemo(() => {
    if (procedureDisplayParts.length > 0) {
      const leaf = String(
        procedureDisplayParts[procedureDisplayParts.length - 1] || ""
      ).trim();
      if (leaf) return leaf;
    }
    const cat = String(displayCategoryLabel || "").trim();
    const sub = String(displaySubCategoryLabel || "").trim();
    if (sub) return sub;
    if (cat) return cat;
    return goalProcedureShort || "—";
  }, [
    procedureDisplayParts,
    displayCategoryLabel,
    displaySubCategoryLabel,
    goalProcedureShort,
  ]);

  const glimpseLocationText = useMemo(
    () => formatCarePriceLocationArea(locationScope, searchPincode, withinZoneRadiusKm),
    [locationScope, searchPincode, withinZoneRadiusKm]
  );

  const glimpseSharedDocCount = useMemo(
    () => displayedList.filter((d) => String(d?.role).toLowerCase() !== "hospital").length,
    [displayedList]
  );
  const glimpseSharedHospCount = useMemo(
    () => displayedList.filter((d) => String(d?.role).toLowerCase() === "hospital").length,
    [displayedList]
  );

  /** First match-results response not received yet (cleared or never loaded). Refetches keep `raw` set so we do not flash skeleton on poll. */
  const showGlimpseProvidersSkeleton =
    showPostSubmitGlimpse && displayedList.length === 0 && matchResultsRaw == null;

  const setPriceByPointerPosition = (clientX) => {
    const sliderEl = priceSliderRef.current;
    if (!sliderEl) return;
    if (!priceBoundsReady) return;

    const rect = sliderEl.getBoundingClientRect();
    if (!rect.width) return;

    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    const rawValue = priceLimits.min + ratio * (priceLimits.max - priceLimits.min);
    setPrice(snapBidToSliderStep(rawValue, priceLimits.min, priceLimits.max));
  };

  return (
    <div className="bid_backgr">
      <div className="bidsec">
        <div
          className={`cpWrap${isPriceSliding ? " cpNoSelect" : ""}${
            showPostSubmitGlimpse ? " cpWrap--postBidGlimpse" : ""
          }`}
        >
          <div className="cpFrame cpFrame--careCompact">
            <>
            <PatientJourneyStrip
              workflow={showPostSubmitGlimpse ? "BIDDING" : "VERIFIED"}
              compact
            />
            <div className="cpGoalLayout cpGoalLayout--compact">
              <main className={`cpGoalMain${showPostSubmitGlimpse ? " cpGoalMain--bidLive" : ""}`}>
                {showPostSubmitGlimpse ? (
                  <>
                    <section
                      className="cpCareGlimpse cpGoalCard cpPanel cpPanel--placeBid"
                      aria-labelledby={
                        showGlimpseProvidersSkeleton ? undefined : "cp-care-glimpse-heading"
                      }
                      aria-label={
                        showGlimpseProvidersSkeleton ? "Loading your live budget summary" : undefined
                      }
                      aria-busy={showGlimpseProvidersSkeleton || undefined}
                    >
                      <div className="cpPanelInner">
                        {showGlimpseProvidersSkeleton ? (
                          <div className="cpBidLiveSkeleton cpBidLiveSkeleton--summary">
                            <SkeletonBlock
                              height={26}
                              width={layoutIsWide ? "42%" : "56%"}
                              className="cpBidLiveSkeleton__summaryTitle"
                            />
                            <SkeletonBlock height={14} width="100%" className="cpBidLiveSkeleton__summaryLead" />
                            <SkeletonBlock
                              height={14}
                              width={layoutIsWide ? "72%" : "88%"}
                              className="cpBidLiveSkeleton__summaryLead"
                            />
                            <dl className="cpBidLiveSkeleton__summaryDl">
                              {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="cpBidLiveSkeleton__summaryRow">
                                  <SkeletonBlock height={12} width={layoutIsWide ? 100 : 88} />
                                  <div className="cpBidLiveSkeleton__summaryDd">
                                    <SkeletonBlock
                                      height={16}
                                      width={i === 0 ? (layoutIsWide ? "100%" : "100%") : "92%"}
                                    />
                                    {i === 0 ? (
                                      <SkeletonBlock
                                        height={14}
                                        width={layoutIsWide ? "85%" : "70%"}
                                        className="cpBidLiveSkeleton__summarySecondLine"
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </dl>
                            <SkeletonBlock
                              height={48}
                              width="100%"
                              className="cpBidLiveSkeleton__summaryBtn cpBidLiveSkeleton__summaryBtnBar"
                            />
                          </div>
                        ) : (
                          <>
                            <h2 id="cp-care-glimpse-heading" className="cpCareGlimpse__title">
                              Your budget is live
                            </h2>
                            <p className="cpCareGlimpse__lead">
                              Your request was shared using the procedure, search area, and budget below.
                            </p>
                            <dl className="cpCareGlimpse__dl">
                              <div className="cpCareGlimpse__row">
                                <dt>Procedure</dt>
                                <dd>{glimpseProcedureText}</dd>
                              </div>
                              <div className="cpCareGlimpse__row">
                                <dt>Location</dt>
                                <dd>{glimpseLocationText}</dd>
                              </div>
                              <div className="cpCareGlimpse__row">
                                <dt>Budget</dt>
                                <dd>
                                  <strong className="cpCareGlimpse__budget">
                                    {fmtINRGoal(glimpseBudgetDisplayAmount)}
                                  </strong>
                                  <span className="cpCareGlimpse__budgetHint"> (your offer)</span>
                                </dd>
                              </div>
                              <div className="cpCareGlimpse__row">
                                <dt>Request shared with</dt>
                                <dd>
                                  <strong>{glimpseSharedHospCount}</strong>{" "}
                                  {glimpseSharedHospCount === 1 ? "hospital" : "hospitals"},{" "}
                                  <strong>{glimpseSharedDocCount}</strong>{" "}
                                  {glimpseSharedDocCount === 1 ? "doctor" : "doctors"}
                                </dd>
                              </div>
                            </dl>
                            <button
                              type="button"
                              className="cpCareGlimpse__editBtn"
                              onClick={() => {
                                setPrice(glimpseBudgetDisplayAmount);
                                setCareGlimpseMode(false);
                              }}
                            >
                              <Pencil size={18} strokeWidth={2} aria-hidden />
                              Edit location and budget
                            </button>
                            <div className="cpCareGlimpse__newBidWrap">
                              <button
                                type="button"
                                className="cpCareGlimpse__newBidBtn"
                                onClick={exitLiveBidForNewPlaceBid}
                              >
                                Share a new budget
                              </button>
                              <p className="cpCareGlimpse__newBidHint">
                                Starts a new request for this procedure. Your previous request stays in your
                                account.
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </section>

                    <section
                      className="cpCareDoctorList cpGoalCard cpPanel cpPanel--placeBid"
                      aria-label="Matched providers"
                      aria-busy={showGlimpseProvidersSkeleton || undefined}
                    >
                      <div className="cpPanelInner">
                        <div className="cpCareDoctorList__head">
                          <span className="cpGoalSectionIconWrap" aria-hidden>
                            <Stethoscope size={20} strokeWidth={2} />
                          </span>
                          {showGlimpseProvidersSkeleton ? (
                            <SkeletonBlock
                              height={18}
                              width={layoutIsWide ? 200 : 168}
                              className="cpBidLiveSkeleton__sectionTitle"
                            />
                          ) : (
                            <h3 className="cpCareDoctorList__title">Doctors &amp; hospitals</h3>
                          )}
                        </div>
                        {showGlimpseProvidersSkeleton ? (
                          <div
                            className="cpBidLiveSkeleton cpBidLiveSkeleton--providers"
                            aria-busy="true"
                            aria-label="Loading doctors and hospitals"
                          >
                            <ul className="cpBidLiveSkeleton__ul">
                              {(layoutIsWide ? [0, 1, 2] : [0, 1]).map((i) => (
                                <li key={i} className="cpBidLiveSkeleton__item">
                                  <SkeletonBlock
                                    className="cpBidLiveSkeleton__avatar"
                                    width={52}
                                    height={52}
                                    pill
                                  />
                                  <div className="cpBidLiveSkeleton__lines">
                                    <SkeletonBlock
                                      height={16}
                                      width={layoutIsWide ? "32%" : "48%"}
                                      className="cpBidLiveSkeleton__line"
                                    />
                                    <SkeletonBlock
                                      height={13}
                                      width={layoutIsWide ? "22%" : "36%"}
                                      className="cpBidLiveSkeleton__line"
                                    />
                                    <SkeletonBlock
                                      height={13}
                                      width={layoutIsWide ? "55%" : "72%"}
                                      className="cpBidLiveSkeleton__line"
                                    />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : displayedList.length === 0 ? (
                          <p className="cpApiNote cpCareDoctorList__empty">
                            Matched providers will appear here as soon as your request reaches them.
                          </p>
                        ) : (
                          <ul className="cpCareDoctorList__ul">
                            {displayedList.map((row, idx) => {
                              const isHosp = String(row?.role).toLowerCase() === "hospital";
                              const actionRaw = String(row?.providerAction || "pending").toLowerCase();
                              const openingExpired = Boolean(row?.openingBidExpired);
                              const statusLabel = patientFacingProviderRowStatusLabel(
                                row?.providerAction,
                                openingExpired
                              );
                              const statusAwaitingReply = actionRaw === "pending" && !openingExpired;
                              return (
                                <li
                                  key={String(row.id || row.bidId || row.providerId || row.name || `row-${idx}`)}
                                  className={`cpCareDoctorList__item${openingExpired ? " cpCareDoctorList__item--expired" : ""}`}
                                >
                                  <div className="cpCareDoctorList__avatarWrap">
                                    {row.avatarUrl ? (
                                      <img src={row.avatarUrl} alt="" className="cpCareDoctorList__avatar" />
                                    ) : (
                                      <span className="cpCareDoctorList__avatarFallback" aria-hidden>
                                        {isHosp ? "H" : "D"}
                                      </span>
                                    )}
                                  </div>
                                  <div className="cpCareDoctorList__body">
                                    <div className="cpCareDoctorList__nameRow">
                                      <p className="cpCareDoctorList__name">{row.name}</p>
                                      {openingExpired ? (
                                        <span className="cpCareDoctorList__expiredPill" title="The time allowed for a first reply from this provider has passed.">
                                          Window ended
                                        </span>
                                      ) : null}
                                    </div>
                                    <p className="cpCareDoctorList__exp">
                                      {[
                                        row.degree,
                                        row.spec && row.spec !== "Specialist" ? row.spec : null,
                                        row.exp ? `${row.exp} yrs exp` : null,
                                        row.hospitalClinicName || null,
                                        row.adminRating ? `Admin ★ ${Number(row.adminRating).toFixed(1)}` : null,
                                        row.patientRatingAverage
                                          ? `Patients ★ ${Number(row.patientRatingAverage).toFixed(1)}${
                                              row.patientRatingCount
                                                ? ` (${row.patientRatingCount})`
                                                : ""
                                            }`
                                          : null,
                                        row.subCategoryName || null,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ") || (row.exp ? `${row.exp} yrs exp` : "")}
                                    </p>
                                    <p className="cpCareDoctorList__status">
                                      Status:{" "}
                                      <span
                                        className={
                                          openingExpired
                                            ? "cpCareDoctorList__statusEm cpCareDoctorList__statusEm--expired"
                                            : statusAwaitingReply
                                              ? "cpCareDoctorList__statusEm cpCareDoctorList__statusEm--awaiting"
                                              : "cpCareDoctorList__statusEm"
                                        }
                                      >
                                        {statusLabel}
                                      </span>
                                      {row.price != null && Number(row.price) > 0 ? (
                                        <>
                                          {" "}
                                          · Budget reply: <strong>{fmtINRGoal(row.price)}</strong>
                                        </>
                                      ) : null}
                                    </p>
                                    {(row.conversationId || row.bidId) && !String(row.bidId || "").startsWith("bid-") ? (
                                      <button
                                        type="button"
                                        className="cpCareDoctorList__chatBtn"
                                        onClick={() => {
                                          const cid = String(row.conversationId || "").trim();
                                          if (cid) {
                                            navigate(`/patient-chats?openConversation=${encodeURIComponent(cid)}`);
                                            return;
                                          }
                                          setInteractModal(row);
                                        }}
                                      >
                                        <MessageCircle size={16} strokeWidth={2.25} aria-hidden />
                                        Open chat
                                      </button>
                                    ) : null}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </section>
                  </>
                ) : null}

                {goalShowProcedure ? (
            <div
              id="cp-care-procedure-anchor"
              className="cpProcedureSummary cpGoalCard cpPanel cpPanel--placeBid"
            >
              <div className="cpPanelInner cpProcedureSummaryInner">
                <div className="cpRowTitle cpRowTitle--split cpGoalSectionTitleRow">
                  <div className="cpGoalSectionTitleMain">
                    <span className="cpGoalSectionIconWrap" aria-hidden>
                      <BriefcaseMedical size={20} strokeWidth={2} />
                    </span>
                    <span>Procedure</span>
                  </div>
                  <button
                    type="button"
                    className="cpProcedureChangeBtn"
                    onClick={() => {
                      const mrId = activeMrIdForProcedureDisplay;
                      if (mrId && /^[a-f0-9]{24}$/i.test(mrId)) {
                        navigate(`/request/${mrId}/category`, {
                          state: { fromCarePrice: true, matchRequestId: mrId },
                        });
                      } else {
                        navigate("/request/new");
                      }
                    }}
                  >
                    {layoutIsWide ? "Change procedure" : "Change"}
                  </button>
                </div>
                {showProcedureSummaryRow ? (
                  <div className="cpProcedureBreadcrumb">
                  <p className="cpProcedureSummaryText">
                    {procedureDisplayParts.length > 0 ? (
                      procedureDisplayParts.map((part, i) => (
                        <React.Fragment key={`${i}-${part}`}>
                          {i > 0 ? <span className="cpProcedureSep"> → </span> : null}
                          <strong>{part}</strong>
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        {displayCategoryLabel ? <strong>{displayCategoryLabel}</strong> : null}
                        {displayCategoryLabel && displaySubCategoryLabel ? (
                          <span className="cpProcedureSep"> → </span>
                        ) : null}
                        {displaySubCategoryLabel ? <strong>{displaySubCategoryLabel}</strong> : null}
                      </>
                    )}
                    {procedureSummaryReady &&
                    procedureDisplayParts.length === 0 &&
                    !displayCategoryLabel &&
                    !displaySubCategoryLabel ? (
                      <>
                        <strong>Procedure on file</strong>
                        <span className="cpProcedureSep"> · </span>
                        <span style={{ fontWeight: 500, color: "#64748b" }}>
                          Labels will appear when your selection syncs.
                        </span>
                      </>
                    ) : null}
                    {!procedureSummaryReady &&
                    procedureDisplayParts.length === 0 &&
                    (displayCategoryLabel || displaySubCategoryLabel) &&
                    !(displayCategoryLabel && displaySubCategoryLabel) ? (
                      <>
                        <span className="cpProcedureSep"> · </span>
                        <span style={{ fontWeight: 500, color: "#64748b" }}>
                          Complete both category and care type so bidding can continue.
                        </span>
                      </>
                    ) : null}
                  </p>
                  </div>
                ) : isLoadingProcedureFromMatchRequest ? (
                  <p className="cpApiNote" style={{ marginTop: "8px", marginBottom: 0 }}>
                    Loading procedure from your care request…
                  </p>
                ) : (
                  <p className="cpApiNote" style={{ marginTop: "8px", marginBottom: 0 }}>
                    No procedure selected yet. Choose a category and care type first so we can load your
                    price range and show matching doctors in the preview.
                  </p>
                )}
              </div>
            </div>
                ) : null}

            {goalShowLocation ? (
            <div className="cpRadiusSection cpRadiusSection--separate cpGoalSection cpGoalSection--location">
              <div className="cpRadiusHeader cpGoalLocHeader">
                <div className="cpGoalLocTitleRow">
                  <span className="cpGoalSectionIconWrap" aria-hidden>
                    <MapPin size={20} strokeWidth={2} />
                  </span>
                  <h3 id="cp-care-location-heading" aria-describedby="cp-care-location-trust">
                    Treatment location
                  </h3>
                </div>
                <p className="cpGoalLocTrust" id="cp-care-location-trust">
                  Only matching doctors and hospitals inside this selected area see your budget.
                </p>
              </div>

              <div className="cpRadiusShell cpRadiusShell--controlsOnly">
                <div className="cpRadiusControlCard">
                  <div className="cpGoalLocCards" role="list">
                    <button
                      type="button"
                      role="listitem"
                      className={`cpGoalLocCard ${locationScope === "withinZone" ? "isActive" : ""}`}
                      onClick={() => {
                        setLocationPermissionError("");
                        if (locationScope !== "withinZone") {
                          setPatientCoords({ patientLat: "", patientLng: "" });
                        }
                        setLocationScope("withinZone");
                      }}
                    >
                      <span className="cpGoalLocCardCheck" aria-hidden>
                        {locationScope === "withinZone" ? <Check size={16} strokeWidth={3} /> : null}
                      </span>
                      <span className="cpGoalLocCardIcon" aria-hidden>
                        <Navigation size={22} />
                      </span>
                      <span className="cpGoalLocCardTitle">Near me</span>
                      <span className="cpGoalLocCardBody">Device location</span>
                    </button>

                    <button
                      type="button"
                      role="listitem"
                      className={`cpGoalLocCard ${
                        locationScope === "allOverWestBengal" ? "isActive" : ""
                      }`}
                      onClick={() => {
                        setLocationScope("allOverWestBengal");
                        setLocationPermissionError("");
                      }}
                    >
                      <span className="cpGoalLocCardCheck" aria-hidden>
                        {locationScope === "allOverWestBengal" ? (
                          <Check size={16} strokeWidth={3} />
                        ) : null}
                      </span>
                      <span className="cpGoalLocCardIcon" aria-hidden>
                        <Globe2 size={22} />
                      </span>
                      <span className="cpGoalLocCardTitle">West Bengal</span>
                      <span className="cpGoalLocCardBody">Statewide</span>
                    </button>

                    <button
                      type="button"
                      role="listitem"
                      className={`cpGoalLocCard ${locationScope === "locationWise" ? "isActive" : ""}`}
                      onClick={() => {
                        setLocationPermissionError("");
                        if (locationScope !== "locationWise") {
                          setPatientCoords({ patientLat: "", patientLng: "" });
                        }
                        setLocationScope("locationWise");
                      }}
                    >
                      <span className="cpGoalLocCardCheck" aria-hidden>
                        {locationScope === "locationWise" ? <Check size={16} strokeWidth={3} /> : null}
                      </span>
                      <span className="cpGoalLocCardIcon" aria-hidden>
                        <MapPin size={22} />
                      </span>
                      <span className="cpGoalLocCardTitle">Pincode</span>
                      <span className="cpGoalLocCardBody">6-digit area</span>
                    </button>
                  </div>

                  {locationScope === "withinZone" &&
                  locationRequesting &&
                  (!patientCoords.patientLat || !patientCoords.patientLng) ? (
                    <p className="cpApiNote cpLocationStatusNote" style={{ marginTop: "4px", marginBottom: "10px" }}>
                      Getting your location…
                    </p>
                  ) : null}

                  {locationScope === "locationWise" && (
                    <div className="cpGoalPincodeField">
                      <input
                        type="text"
                        className="cpGoalPincodeInput"
                        value={searchPincode}
                        onChange={(e) =>
                          setSearchPincode(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        onBlur={() => setPreviewSearchPincode(searchPincode.trim())}
                        placeholder="Enter area pincode"
                        inputMode="numeric"
                        autoComplete="postal-code"
                      />
                    </div>
                  )}

                  {locationScope === "withinZone" ? (
                    <div className="cpRadiusKmBlock">
                      <div className="cpRadiusKmHeader">
                        <span>Treatment radius</span>
                        <strong>{withinZoneRadiusKm} km</strong>
                      </div>
                      <input
                        type="range"
                        className="cpRange cpRange--zoneKm"
                        min={5}
                        max={WITHIN_ZONE_SLIDER_MAX_KM}
                        step={1}
                        value={withinZoneRadiusKm}
                        onChange={(e) => setWithinZoneRadiusKm(Number(e.target.value))}
                        onMouseUp={(e) =>
                          setPreviewWithinZoneRadiusKm(Number(e.currentTarget.value))
                        }
                        onTouchEnd={(e) =>
                          setPreviewWithinZoneRadiusKm(Number(e.currentTarget.value))
                        }
                        aria-valuemin={5}
                        aria-valuemax={WITHIN_ZONE_SLIDER_MAX_KM}
                        aria-valuenow={withinZoneRadiusKm}
                        aria-label="Treatment radius in kilometers"
                        style={{
                          "--bgSize": `${
                            ((withinZoneRadiusKm - 5) / (WITHIN_ZONE_SLIDER_MAX_KM - 5)) * 100
                          }%`,
                        }}
                      />
                      <div className="cpGoalKmAxis" aria-hidden>
                        <span>5 km</span>
                        <span>50 km</span>
                      </div>
                      <p className="cpRadiusNote cpRadiusNote--tight">
                        Matches use providers within this radius of your location.
                      </p>
                    </div>
                  ) : null}

                  <div
                    className="cpGoalMatchBand"
                    aria-live="polite"
                    aria-label={`${previewHospCount} hospitals, ${previewDocCount} doctors`}
                  >
                    <p className="cpGoalMatchBandInline">
                      Estimated matches within{" "}
                      {locationScope === "withinZone"
                        ? `${withinZoneRadiusKm} km`
                        : locationScope === "locationWise"
                          ? searchPincode.trim()
                            ? `pincode ${searchPincode.trim()}`
                            : "your area (enter pincode)"
                          : "West Bengal"}
                      {": "}
                      <strong>{previewHospCount}</strong>{" "}
                      {previewHospCount === 1 ? "hospital" : "hospitals"}, <strong>{previewDocCount}</strong>{" "}
                      {previewDocCount === 1 ? "doctor" : "doctors"}.
                    </p>
                  </div>

                  {showWithinZoneZeroPreviewHelp ? (
                    <p className="cpApiNote" style={{ marginTop: "10px", marginBottom: 0 }}>
                      No providers in preview for this procedure within ~{withinZoneRadiusKm} km of your
                      location. Doctors who match the procedure but practice farther away (or outside this
                      radius) are excluded here — use <strong>All Over WB</strong> or{" "}
                      <strong>Search Location</strong> to widen results.
                    </p>
                  ) : null}

                  {!!locationPermissionError && (
                    <div className="cpApiNote" style={{ marginTop: "10px" }}>
                      {locationPermissionError}
                    </div>
                  )}
                </div>
              </div>

            </div>
            ) : null}

            {goalShowBudget ? (
              <>
              
            <div className="cpPanel cpPanel--placeBid cpGoalSection cpGoalSection--package">
              <div className="cpPanelInner">
                <div className="cpRowTitle cpGoalBudgetTitle">
                  <div className="cpGoalSectionTitleMain">
                    <span className="cpGoalSectionIconWrap" aria-hidden>
                      <ClipboardList size={20} strokeWidth={2} />
                    </span>
                    <span>What’s included</span>
                  </div>
                </div>
                <p className="cpGoalLocTrust">
                  Tell doctors what your budget should cover so replies are comparable.
                </p>
                <label className="cpPackageCheck">
                  <input
                    type="checkbox"
                    checked={packageProcedureOt}
                    onChange={(e) => setPackageProcedureOt(e.target.checked)}
                  />
                  <span>Procedure / OT fees</span>
                </label>
                <label className="cpPackageCheck">
                  <input
                    type="checkbox"
                    checked={packageImplants}
                    onChange={(e) => setPackageImplants(e.target.checked)}
                  />
                  <span>Implants / consumables</span>
                </label>
                <label className="cpPackageCheck">
                  <input
                    type="checkbox"
                    checked={packageHospitalStay}
                    onChange={(e) => setPackageHospitalStay(e.target.checked)}
                  />
                  <span>Hospital stay</span>
                </label>
              </div>
            </div>
<div
                id="cp-care-budget-anchor"
                className="cpPanel cpPanel--placeBid cpBudgetAfterLocation cpGoalSection cpGoalSection--budget"
              >
                <div className="cpPanelInner">
                  <div className="cpSliderBlock">
                    <div className="cpRowTitle cpGoalBudgetTitle">
                      <div className="cpGoalSectionTitleMain">
                        <span className="cpGoalSectionIconWrap" aria-hidden>
                          <Wallet size={20} strokeWidth={2} />
                        </span>
                        <span>Budget</span>
                      </div>
                    </div>

                    {recommendedBand ? (
                      <div className="cpGoalRecBand cpGoalRecBand--budget">
                        <div className="cpGoalRecBandLabel">Suggested range</div>
                        <div className="cpGoalRecBandValues">
                          {fmtINRGoal(recommendedBand.lo)} – {fmtINRGoal(recommendedBand.hi)}
                        </div>
                      </div>
                    ) : null}

                    {!priceBoundsReady ? (
                      <div className="cpApiNote" style={{ marginTop: "14px" }}>
                        {typeof error === "string" && error.trim()
                          ? `Could not load price range: ${error}`
                          : procedureSummaryReady
                            ? "Loading price range..."
                            : "Select a procedure in Step 1 to load a price range for this care type."}
                      </div>
                    ) : (
                      <div className="cpBudgetSliderShell">
                        <div className="cpGoalBudgetBand">
                          <div className="cpGoalBudgetOfferRow">
                            <span className="cpGoalBudgetOfferLabel">Your budget</span>
                            {recommendedBand ? (
                              <span
                                className={`cpGoalBudgetStatus ${
                                  withinSuggestedBudget
                                    ? "cpGoalBudgetStatus--in"
                                    : "cpGoalBudgetStatus--out"
                                }`}
                              >
                                {withinSuggestedBudget
                                  ? "In suggested range"
                                  : "Outside suggested range"}
                              </span>
                            ) : null}
                          </div>
                          <div
                            className="cpGoalBudgetDisplay"
                            aria-live={isPriceSliding ? "off" : "polite"}
                          >
                            {fmtINRGoal(safePrice)}
                          </div>
                          <p className="cpGoalBudgetCaption">
                            Providers see this amount with your request.
                          </p>
                          <div className="cpRangeWrap cpRangeWrap--placeBid">
                            <div
                              className={`cpBudgetSliderTrackWrap${
                                budgetRecTrackStyle ? " cpBudgetSliderTrackWrap--hasRec" : ""
                              }`}
                              style={budgetRecTrackStyle}
                            >
                              <input
                                ref={priceSliderRef}
                                className="cpRange cpBudgetRange"
                                style={{ "--bgSize": priceFill }}
                                type="range"
                                min={priceLimits.min}
                                max={priceLimits.max}
                                step={priceSliderStep}
                                value={safePrice}
                                aria-label="Your surgery budget"
                                aria-valuetext={fmtINRGoal(safePrice)}
                                onMouseDown={() => setIsPriceSliding(true)}
                                onMouseUp={() => setIsPriceSliding(false)}
                                onPointerDown={() => setIsPriceSliding(true)}
                                onPointerUp={() => setIsPriceSliding(false)}
                                onPointerCancel={() => setIsPriceSliding(false)}
                                onTouchStart={() => setIsPriceSliding(true)}
                                onTouchEnd={() => setIsPriceSliding(false)}
                                onClick={(e) => setPriceByPointerPosition(e.clientX)}
                                onChange={(e) =>
                                  setPrice(
                                    snapBidToSliderStep(
                                      Number(e.target.value),
                                      priceLimits.min,
                                      priceLimits.max
                                    )
                                  )
                                }
                                onInput={(e) =>
                                  setPrice(
                                    snapBidToSliderStep(
                                      Number(e.target.value),
                                      priceLimits.min,
                                      priceLimits.max
                                    )
                                  )
                                }
                              />
                            </div>
                          </div>

                          <div className="cpRangeMeta cpRangeMeta--budgetGoal">
                            <span className="cpRangeMetaBound">
                              <span className="cpRangeMetaLabel">Min</span>
                              <span className="cpMin">{fmtINRGoal(priceLimits.min)}</span>
                            </span>
                            <span className="cpRangeMetaBound cpRangeMetaBound--max">
                              <span className="cpRangeMetaLabel">Max</span>
                              <span className="cpMax">{fmtINRGoal(priceLimits.max)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="cpControlBox cpControlBox--placeBid">
                    <button
                      type="button"
                      className="cpCtlBtn"
                      onClick={() =>
                        priceBoundsReady
                          ? setPrice((p) =>
                              snapBidToSliderStep(
                                p - priceNudgeStep,
                                priceLimits.min,
                                priceLimits.max
                              )
                            )
                          : null
                      }
                      disabled={!priceBoundsReady}
                      title={`Decrease by ${fmtINRGoal(priceNudgeStep)}`}
                    >
                      − {fmtINRGoal(priceNudgeStep)}
                    </button>

                    <button
                      type="button"
                      className="cpCtlBtn cpReset"
                      onClick={resetToAverage}
                      disabled={!priceBoundsReady}
                    >
                      Reset
                    </button>

                    <button
                      type="button"
                      className="cpCtlBtn"
                      onClick={() =>
                        priceBoundsReady
                          ? setPrice((p) =>
                              snapBidToSliderStep(
                                p + priceNudgeStep,
                                priceLimits.min,
                                priceLimits.max
                              )
                            )
                          : null
                      }
                      disabled={!priceBoundsReady}
                      title={`Increase by ${fmtINRGoal(priceNudgeStep)}`}
                    >
                      + {fmtINRGoal(priceNudgeStep)}
                    </button>
                  </div>
                </div>
              </div>

              <div className="cpStickySubmit cpGoalStickySubmit">
                {requestSubmitted && !careGlimpseMode ? (
                  <div className="cpSubmitWrap cpSubmitWrap--separate cpSubmitWrap--stacked">
                    <button
                      type="button"
                      className="cpSubmitRequestBtn cpSubmitRequestBtn--secondaryWide"
                      onClick={() => setCareGlimpseMode(true)}
                    >
                      View summary
                    </button>
                    <button
                      type="button"
                      className="cpSubmitRequestBtn cpSubmitRequestBtn--withIcon"
                      onClick={exitLiveBidForNewPlaceBid}
                      disabled={isSubmittingRequest}
                    >
                      Share a new budget <Send size={18} strokeWidth={2.25} aria-hidden />
                    </button>
                    <p className="cpApiNote cpSubmitWrap__liveBidNote">
                      Shared requests cannot be edited in place. Use &ldquo;Share a new budget&rdquo; to send a
                      fresh budget request, or open your dashboard to review the existing one.
                    </p>
                  </div>
                ) : !showPostSubmitGlimpse ? (
                  <div className="cpSubmitWrap cpSubmitWrap--separate">
                    <button
                      type="button"
                      className="cpSubmitRequestBtn cpSubmitRequestBtn--withIcon"
                      onClick={handleSubmitRequest}
                      disabled={submitRequestDisabled}
                    >
                      {isSubmittingRequest ? (
                        isPendingAdminReview ? "Saving…" : "Sharing budget…"
                      ) : (
                        <>
                          {isPendingAdminReview
                            ? "Save budget"
                            : isVerifiedReadyToNotify
                              ? "Notify matching doctors"
                              : "Share my budget"}{" "}
                          <Send size={18} strokeWidth={2.25} aria-hidden />
                        </>
                      )}
                    </button>
                    <p className="cpApiNote">
                      {isPendingAdminReview
                        ? "Admin is still checking your documents. You can save or update your budget now — doctors won’t see it until after approval and your confirm."
                        : isVerifiedReadyToNotify
                          ? "Confirm your draft, then notify matching doctors — replies arrive in Messages. You don’t pay yet."
                          : "You don’t pay yet. You’re only saving a draft budget for later."}
                    </p>
                    {submitRequestDisabled && submitRequestDisabledHint ? (
                      <p className="cpApiNote">{submitRequestDisabledHint}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              </>
            ) : null}
              </main>

            </div>

            {!layoutIsWide && wizardProcedureSatisfied && goalMobileStep === 1 && !showPostSubmitGlimpse ? (
              <div className="cpGoalDock">
                <div className="cpGoalDockInner">
                  <div className="cpGoalDockMeta">
                    <span className="cpGoalDockIconWrap" aria-hidden>
                      <ClipboardList size={18} />
                    </span>
                    <div className="cpGoalDockLines">
                      <div>
                        <Sparkles size={12} className="cpGoalDockInlineIcon" aria-hidden />{" "}
                        <span className="cpGoalDockLineClamp">{goalProcedureShort || "Your procedure"}</span>
                      </div>
                      <div>{fmtINRGoal(safePrice)} budget</div>
                      <div className="cpGoalDockHint">Next: location</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cpGoalDockCta"
                    onClick={() => {
                      if (!wizardProcedureSatisfied) navigate("/request/new");
                      else setCareWizardStep(2);
                    }}
                  >
                    {!wizardProcedureSatisfied ? (
                      <>
                        Choose procedure <ChevronRight size={18} aria-hidden />
                      </>
                    ) : (
                      <>
                        Continue to location <ChevronRight size={18} aria-hidden />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : null}

            </>

            {!!(submitRequestError || error) && (
              <div className="cpApiNote">{submitRequestError || error}</div>
            )}
            {!!successMessage && !submitRequestError && !error && (
              <div className="cpApiNote">{successMessage}</div>
            )}

            {/* Interact / Bid History chat popup */}
            {interactModal?.bidId && (
              <BidHistoryChat
                open={!!interactModal}
                onClose={() => setInteractModal(null)}
                mode="patient"
                bidId={interactModal.bidId}
                providerName={interactModal.name}
                peerAvatarUrl={interactModal.avatarUrl}
                initialBid={{
                  affordabilityPrice:
                    interactModal.patientOfferAmount ??
                    interactModal.yourBidPrice ??
                    toFiniteNumber(interactModal.raw?.bid?.affordabilityPrice) ??
                    price,
                  providerAction: interactModal.providerAction,
                  reverseBidAmount:
                    interactModal.reverseBidAmount ??
                    interactModal.reverseBidPrice ??
                    toFiniteNumber(interactModal.raw?.bid?.reverseBidAmount) ??
                    (String(interactModal.providerAction || "").toLowerCase() === "countered"
                      ? toFiniteNumber(interactModal.price)
                      : null),
                  patientAction: interactModal.patientAction,
                  patientCounterAmount: interactModal.patientCounterAmount,
                }}
                requestId={pickId(
                  matchRequest?.matchRequestId,
                  matchRequest?._id,
                  matchRequest?.id,
                  activeMatchRequestId
                )}
                dispatch={dispatch}
                getMatchResults={getMatchResults}
                getProviderCount={getProviderCount}
                respondToBid={respondToBid}
                patientCounterBid={patientCounterBid}
                bookAppointment={bookAppointment}
                onBidUpdated={() => {
                  const reqId = pickId(matchRequest?.matchRequestId, matchRequest?._id, matchRequest?.id, activeMatchRequestId);
                  if (reqId) {
                    dispatch(getMatchResults(reqId));
                    dispatch(getProviderCount(reqId));
                  }
                }}
              />
            )}

            {/* Reverse Bid modal (matches Doctor Dashboard reverse bid popup) */}
            {reverseBidModal && (
              <div
                className="doctor_bid_modal_backdrop"
                onClick={() => !counterSubmitting && setReverseBidModal(null)}
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="doctor_bid_modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>Suggest another amount</h3>
                  <p className="doctor_bid_modal_sub">
                    Patient: {reverseBidModal.patientName}. Current bid:{" "}
                    {reverseBidModal.currentBid != null
                      ? fmtINR(reverseBidModal.currentBid)
                      : "—"}
                  </p>

                  <label>
                    Your counter amount (Rs):{" "}
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 6,000"
                      value={reverseBidModal.amount ?? ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (bidAmountRawHasDisallowedSymbols(raw)) return;
                        setReverseBidModal((m) => (m ? { ...m, amount: formatIndianBidAmountInput(raw) } : m));
                        setReverseBidError("");
                      }}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                      }}
                      disabled={counterSubmitting}
                    />
                  </label>
                  {!!reverseBidError && (
                    <div className="doctor_bid_modal_error">{reverseBidError}</div>
                  )}

                  <div className="doctor_bid_modal_actions">
                    <button
                      type="button"
                      className="doctor_bid_modal_btn secondary"
                      onClick={() =>
                        !counterSubmitting && setReverseBidModal(null)
                      }
                      disabled={counterSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="doctor_bid_modal_btn primary"
                      onClick={submitReverseBidModal}
                      disabled={counterSubmitting}
                    >
                      {counterSubmitting ? "Submitting…" : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
